<template>
  <teleport to="body">
    <transition name="fade">
      <div
        v-if="visible"
        class="slideshow-overlay"
        :class="{ minimized: isMinimized }"
        @click.self="isMinimized ? expandOverlay() : handleClose()"
        @keydown.esc="isMinimized ? expandOverlay() : handleClose()"
        @keydown.left="prevImage"
        @keydown.right="nextImage"
        @keydown.up.prevent="navigateToPrevPost"
        @keydown.down.prevent="navigateToNextPost"
        @touchstart.passive="onTouchStart"
        @touchmove.passive="onTouchMove"
        @touchend.passive="onTouchEnd"
        @wheel="onWheel"
        :aria-busy="itemNavigationLoading ? 'true' : 'false'"
        tabindex="0"
        ref="overlayRef"
      >
        <!-- Close button -->
        <button
          v-if="!isMinimized"
          type="button"
          class="slideshow-close"
          aria-label="Close overlay"
          @click.stop="handleClose()"
          v-haptic
        >
          <svg
            class="slideshow-close__icon"
            aria-hidden="true"
            focusable="false"
          >
            <use href="@/assets/icons/iconset.svg#cross"></use>
          </svg>
        </button>

        <!-- Navigation Arrows (Moved outside content to be fixed to screen edges) -->
        <!-- Previous arrow -->
        <button
          v-if="showNavigationArrows && !isMinimized"
          class="slideshow-nav slideshow-prev"
          @click.stop="prevImage"
          :disabled="currentIndex === 0"
          aria-label="Previous media"
        >
          <svg class="compactImg">
            <use href="@/assets/icons/iconset.svg#arrowleft_carousel_compact"></use>
          </svg>
        </button>

        <!-- Next arrow -->
        <button
          v-if="showNavigationArrows && !isMinimized"
          class="slideshow-nav slideshow-next"
          @click.stop="nextImage"
          :disabled="currentIndex === totalMultiSlides - 1"
          aria-label="Next media"
        >
          <svg class="compactImg">
            <use href="@/assets/icons/iconset.svg#arrowright_carousel_compact"></use>
          </svg>
        </button>

        <!-- Image container (animated when minimizing/expanding) -->
        <transition :name="contentTransitionName">
        <div
          v-show="!isMinimized"
          :key="item?.id || 'empty-item'"
          class="slideshow-content"
          :class="{
            'blog-mode': isArticlePresentation,
            'story-mode': isStoryPresentation,
            'gallery-mode': isGalleryPresentation,
          }"
          @click.stop
        >
          <!-- Main media (image, video, or audio) -->
          <div
            class="slideshow-image-wrapper"
            :class="{
              'blog-mode': isArticlePresentation,
              'story-mode': isStoryPresentation,
              'gallery-mode': isGalleryPresentation,
            }"
          >
            <!-- EMBED ITEM -->
            <div v-if="isEmbedItem" class="embed-slideshow-container" :class="{ 'soundcloud-embed': isSoundCloudEmbed }">
              <div v-if="isSpotifyEmbed" class="spotify-link-card spotify-link-card--overlay">
                <span class="spotify-link-card__eyebrow">Spotify</span>
                <h3 class="spotify-link-card__title">
                  {{ displayTitle || "Open this item in Spotify" }}
                </h3>
                <a
                  v-if="embedExternalUrl"
                  :href="embedExternalUrl"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="spotify-link-card__button"
                >
                  Open on Spotify
                </a>
              </div>
              <div
                v-else
                class="embed-slideshow-player"
                :class="{ 'embed-vertical': isVerticalEmbedItem }"
                v-html="sanitizedEmbedOverlay"
              ></div>
            </div>
            <!-- POSTER ITEM: Text-only, uses unified title at top -->
            <div v-else-if="isPosterItem" class="poster-slideshow-container">
              <div class="poster-content">
                <!-- Title shown at top via unified slideshow-title-top -->
              </div>
            </div>
            <!-- TWEET ITEM: Short text (≤140 chars) with optional image -->
            <div v-else-if="isTweetItem" class="tweet-slideshow-container">
              <img
                v-if="tweetBackgroundImage"
                :src="tweetBackgroundImage"
                :alt="displayTitle"
                class="tweet-background-image"
              />
              <div
                class="tweet-content"
                :class="{ 'with-image': tweetBackgroundImage }"
              >
                <!-- Title shown at top via unified slideshow-title-top -->
                <p v-if="displayDescription" class="tweet-text">
                  <LinkifiedText :text="displayDescription" />
                </p>
              </div>
            </div>
            <!-- COMPLEX POST: Scrollable blog-style layout (media grid + text) -->
            <div
              v-else-if="isArticlePresentation"
              class="post-complex-container"
              @click.stop
            >
              <!-- Title (ImageCard-style) -->
              <div v-if="displayTitle" class="post-complex-title">
                {{ displayTitle }}
              </div>
              <!-- Media grid (if multiple media items) -->
              <div
                v-if="postMediaItemsForGrid.length > 1"
                class="post-complex-media-grid"
              >
                <div
                  v-for="(mediaItem, mIdx) in postMediaItemsForGrid"
                  :key="'grid-' + mIdx"
                  class="post-complex-grid-item"
                  :class="{ 'grid-item-landscape': mediaAspectByUrl[mediaItem.url] > 1.2 }"
                  @click.stop="onOrganicMediaClick({ item: mediaItem, index: mIdx })"
                >
                  <div class="post-complex-grid-media-wrap">
                    <video
                      v-if="mediaItem.type === 'video'"
                      :src="mediaItem.url"
                      :poster="mediaItem.poster"
                      class="post-complex-grid-element"
                      preload="metadata"
                      playsinline
                      muted
                      @loadeddata="onMediaLoad"
                    />
                    <div v-if="mediaItem.type === 'video'" class="post-complex-play-overlay">
                      <svg class="post-complex-play-icon" viewBox="0 0 24 24" aria-hidden="true">
                        <path d="M8 5v14l11-7z" fill="currentColor" />
                      </svg>
                    </div>
                    <img
                      v-else
                      :src="mediaItem.url"
                      :alt="mediaItem.caption || 'Media'"
                      class="post-complex-grid-element"
                      loading="lazy"
                      @load="onMediaLoad"
                    />
                  </div>
                  <small v-if="mediaItem.caption" class="post-complex-caption">
                    {{ truncateCaption(mediaItem.caption) }}
                  </small>
                </div>
              </div>
              <!-- Single media hero (if exactly one media item) -->
              <div v-else-if="postMediaItemsForGrid.length === 1" class="post-complex-hero">
                <video
                  v-if="postMediaItemsForGrid[0].type === 'video'"
                  :src="postMediaItemsForGrid[0].url"
                  controls
                  playsinline
                  class="post-complex-hero-element"
                  @loadeddata="onMediaLoad"
                ></video>
                <img
                  v-else
                  :src="postMediaItemsForGrid[0].url"
                  :alt="displayTitle"
                  class="post-complex-hero-element"
                  @load="onMediaLoad"
                />
                <small v-if="postMediaItemsForGrid[0].caption" class="post-complex-caption">
                  {{ truncateCaption(postMediaItemsForGrid[0].caption) }}
                </small>
              </div>
              <!-- Scrollable text body -->
              <div v-if="displayDescription" class="post-complex-body">
                <template
                  v-for="block in blogContentBlocks"
                  :key="block.key"
                >
                  <!-- YouTube embed block -->
                  <div
                    v-if="block.type === 'youtube'"
                    class="post-complex-embed-wrapper"
                    v-html="block.html"
                  ></div>
                  <!-- Text paragraph block -->
                  <div v-else class="post-complex-paragraph">
                    <LinkifiedText :text="block.text" />
                  </div>
                </template>
              </div>
            </div>
            <!-- STORY: One visual media item without a meaningful authored body -->
            <div
              v-else-if="isStoryPresentation"
              class="post-story-container"
              :class="{ 'story-mobile-cover': storyUsesMobileCover }"
            >
              <div v-if="storyMediaItem" class="post-story-stage">
                <video
                  v-if="storyMediaItem.type === 'video'"
                  ref="videoRef"
                  :src="storyMediaItem.url"
                  :poster="storyMediaItem.poster"
                  controls
                  playsinline
                  class="post-story-media"
                  @loadedmetadata="onMediaLoad"
                ></video>
                <img
                  v-else
                  :src="storyMediaItem.url"
                  :alt="displayTitle"
                  class="post-story-media"
                  @load="onMediaLoad"
                />
                <div v-if="isLoading" class="slideshow-loading">
                  <div class="slideshow-spinner"></div>
                </div>
              </div>
              <div v-if="displayDescription" class="post-story-description">
                <LinkifiedText :text="displayDescription" />
              </div>
            </div>

            <!-- GALLERY: Desktop carousel; mobile single-column vertical scroller -->
            <div
              v-else-if="isGalleryPresentation"
              class="post-gallery-container"
            >
              <div v-if="!isMobileViewport" class="post-gallery-carousel">
                <transition :name="slideDirection" mode="out-in">
                  <figure
                    v-if="currentGalleryItem"
                    :key="currentGalleryItem.url"
                    class="post-gallery-item post-gallery-item--active"
                  >
                    <video
                      v-if="currentGalleryItem.type === 'video'"
                      ref="galleryVideoRefs"
                      :src="currentGalleryItem.url"
                      :poster="currentGalleryItem.poster"
                      controls
                      playsinline
                      preload="metadata"
                      class="post-gallery-media"
                      @loadedmetadata="onMediaLoad"
                    ></video>
                    <img
                      v-else
                      :src="currentGalleryItem.url"
                      :alt="currentGalleryItem.caption || displayTitle"
                      class="post-gallery-media"
                      @load="onMediaLoad"
                    />
                    <div v-if="isLoading" class="slideshow-loading">
                      <div class="slideshow-spinner"></div>
                    </div>
                    <figcaption
                      v-if="currentGalleryItem.caption"
                      class="post-gallery-caption"
                    >
                      {{ currentGalleryItem.caption }}
                    </figcaption>
                  </figure>
                </transition>
                <div
                  v-if="displayDescription"
                  class="post-gallery-description"
                >
                  <LinkifiedText :text="displayDescription" />
                </div>
              </div>

              <div
                v-else
                class="post-gallery-mobile-list"
                @scroll.passive="onGalleryMobileScroll"
              >
                <div
                  v-if="displayDescription"
                  class="post-gallery-mobile-description"
                >
                  <LinkifiedText :text="displayDescription" />
                </div>
                <figure
                  v-for="(mediaItem, mediaIndex) in galleryMediaItems"
                  :key="`${mediaItem.url}-${mediaIndex}`"
                  class="post-gallery-item"
                  :data-gallery-index="mediaIndex"
                >
                  <video
                    v-if="mediaItem.type === 'video'"
                    ref="galleryVideoRefs"
                    :src="mediaItem.url"
                    :poster="mediaItem.poster"
                    controls
                    playsinline
                    preload="metadata"
                    class="post-gallery-media"
                    @loadedmetadata="onMediaLoad"
                  ></video>
                  <img
                    v-else
                    :src="mediaItem.url"
                    :alt="mediaItem.caption || displayTitle"
                    class="post-gallery-media"
                    loading="lazy"
                    @load="onMediaLoad"
                  />
                  <figcaption
                    v-if="mediaItem.caption"
                    class="post-gallery-caption"
                  >
                    {{ mediaItem.caption }}
                  </figcaption>
                </figure>
              </div>
            </div>
            <!-- POST ITEM: Text-only (no media) -->
            <div
              v-else-if="
                isTextStoryPresentation ||
                (isPostItem && postMediaItems.length === 0)
              "
              class="post-slideshow-container"
            >
              <div class="post-text-section">
                <div class="post-description">
                  <LinkifiedText :text="displayDescription" />
                </div>
              </div>
            </div>
            <!-- DOCUMENTS ITEM: PDF embed, text preview, or fallback card -->
            <div
              v-else-if="isDocumentItem"
              class="document-slideshow-container"
            >
              <!-- PDF: inline embed viewer -->
              <template v-if="isPdfDocument && documentUrl">
                <div class="document-pdf-viewer">
                  <embed
                    :src="documentUrl"
                    type="application/pdf"
                    class="document-pdf-embed"
                  />
                </div>
              </template>
              <!-- Text file: syntax-highlighted preview -->
              <template v-else-if="isTextDocument && documentTextContent">
                <div class="document-text-viewer">
                  <div class="document-text-header">
                    <span v-if="documentExtension" class="document-type-badge">{{ documentExtension }}</span>
                    <span class="document-text-name">{{ documentFilename }}</span>
                  </div>
                  <pre class="document-text-body">{{ documentTextContent }}</pre>
                </div>
              </template>
              <!-- Fallback: icon + filename + extension badge -->
              <template v-else>
                <div class="document-icon-wrapper">
                  <svg
                    class="document-icon"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <use href="@/assets/icons/iconset.svg#file"></use>
                  </svg>
                </div>
                <div class="document-info">
                  <span v-if="documentExtension" class="document-type-badge">{{ documentExtension }}</span>
                  <span class="document-filename">{{ documentFilename }}</span>
                </div>
              </template>
              <!-- Description (all document sub-types) -->
              <div
                v-if="displayDescription"
                ref="docDescriptionRef"
                class="document-description"
                :class="{ 'is-overflowing': docDescOverflows }"
              >
                <LinkifiedText :text="displayDescription" />
              </div>
            </div>
            <!-- NFT ITEM: Complex-post-style with detail panel -->
            <div
              v-else-if="isNFTType"
              class="post-complex-container nft-overlay"
              @click.stop
            >
              <!-- Title -->
              <div v-if="displayTitle" class="post-complex-title">
                {{ displayTitle }}
              </div>
              <div v-if="item.collection" class="nft-collection-label">
                {{ item.collection }}
              </div>

              <!-- Hero media -->
              <div class="post-complex-hero">
                <img
                  v-if="currentMediaUrl"
                  :src="currentMediaUrl"
                  :alt="displayTitle"
                  class="post-complex-hero-element"
                  @load="onMediaLoad"
                />
                <div v-if="isLoading" class="slideshow-loading">
                  <div class="slideshow-spinner"></div>
                </div>
              </div>

              <!-- Description -->
              <div v-if="displayDescription" class="post-complex-body">
                <div class="post-complex-paragraph">
                  <LinkifiedText :text="displayDescription" />
                </div>
              </div>

              <!-- Item Detail Panel (NFT metadata) -->
              <ItemDetailPanel
                :item="item"
                item-type="nft"
                :formatted-price="formattedPrice"
                :display-coin-symbol="displayCoinSymbol"
                :is-owner="isOwner"
                :nft-links="ipfsLinks"
              />

              <!-- Make Offer / Buy Now button (for non-owners viewing any NFT) -->
              <UnitButton
                v-if="!isOwner && currentUserUid"
                type="button"
                variant="secondary"
                size="small"
                :class="['make-offer-button', { 'confirm-phase': buyConfirmPending && item.listingPrice }]"
                @click.stop="handleBuyOfferClick"
              >
                <template v-if="item.listingPrice && buyConfirmPending">
                  Confirm Buy?
                </template>
                <template v-else>
                  {{ item.listingPrice ? 'Buy Now' : 'Make Offer' }}
                </template>
                <template #trailing>
                  <svg class="compactImg" aria-hidden="true">
                    <use href="@/assets/icons/iconset.svg#message_money_compact"></use>
                  </svg>
                </template>
              </UnitButton>
            </div>
            <!-- PRODUCT ITEM: Mimic ProductView -->
            <div v-else-if="isProductItem" class="product-slideshow-container">
              <!-- Image gallery -->
              <div class="product-gallery">
                <img
                  v-if="currentMediaUrl"
                  :src="currentMediaUrl"
                  :alt="displayTitle"
                  class="product-image"
                  @load="onMediaLoad"
                />
                <div v-if="isLoading" class="slideshow-loading">
                  <div class="slideshow-spinner"></div>
                </div>
              </div>
              <!-- Product info panel -->
              <div class="product-info-panel">
                <!-- Title shown at top via unified slideshow-title-top -->
                <div class="product-price">
                  <AuthorityListingPurchase
                    v-if="isAuthorityPresentation"
                    :item="item"
                    :actor-uid="currentUserUid"
                    @purchased="handleAuthorityPurchaseSettled"
                  />
                  <template v-else>
                    <span class="price-amount">{{ formattedPrice }}</span>
                    <span v-if="displayCoinSymbol" class="price-currency">{{
                      displayCoinSymbol
                    }}</span>
                  </template>
                </div>
                <p v-if="displayDescription" class="product-description">
                  <LinkifiedText :text="displayDescription" />
                </p>
                <div
                  v-if="item.existingStock !== undefined"
                  class="product-stock"
                >
                  Stock: {{ item.existingStock }}
                </div>
                <div v-if="item.condition" class="product-condition">
                  Condition: {{ item.condition }}
                </div>
                <!-- Item Detail Panel (Product metadata) -->
                <ItemDetailPanel
                  :item="item"
                  item-type="product"
                  :formatted-price="formattedPrice"
                  :display-coin-symbol="displayCoinSymbol"
                  :is-owner="isOwner"
                />
                <!-- Make Offer button (for non-owners) -->
                <UnitButton
                  v-if="!isAuthorityPresentation && !isOwner && currentUserUid"
                  type="button"
                  variant="secondary"
                  size="small"
                  class="make-offer-button"
                  @click.stop="handleMakeOffer"
                >
                  Make Offer
                  <template #trailing>
                    <svg class="compactImg" aria-hidden="true">
                      <use
                        href="@/assets/icons/iconset.svg#message_money_compact"
                      ></use>
                    </svg>
                  </template>
                </UnitButton>
              </div>
            </div>
            <!-- SERVICE ITEM: Mimic ProductView service mode -->
            <div v-else-if="isServiceItem" class="service-slideshow-container">
              <!-- Image gallery -->
              <div class="service-gallery">
                <img
                  v-if="currentMediaUrl"
                  :src="currentMediaUrl"
                  :alt="displayTitle"
                  class="service-image"
                  @load="onMediaLoad"
                />
                <div v-if="isLoading" class="slideshow-loading">
                  <div class="slideshow-spinner"></div>
                </div>
              </div>
              <!-- Service info panel -->
              <div class="service-info-panel">
                <!-- Title shown at top via unified slideshow-title-top -->
                <div class="service-price">
                  <AuthorityListingPurchase
                    v-if="isAuthorityPresentation"
                    :item="item"
                    :actor-uid="currentUserUid"
                    @purchased="handleAuthorityPurchaseSettled"
                  />
                  <template v-else>
                    <span class="price-amount">{{ formattedPrice }}</span>
                    <span v-if="displayCoinSymbol" class="price-currency">{{
                      displayCoinSymbol
                    }}</span>
                  </template>
                </div>
                <p v-if="displayDescription" class="service-description">
                  <LinkifiedText :text="displayDescription" />
                </p>
                <div v-if="item.serviceDuration" class="service-duration">
                  Duration: {{ item.serviceDuration }} min
                </div>
                <div
                  v-if="item.isAvailableOnline !== undefined"
                  class="service-availability"
                >
                  {{
                    item.isAvailableOnline
                      ? "Available Online"
                      : "In-person only"
                  }}
                </div>

                <!-- Booking Table -->
                <div
                  v-if="availableDays.length > 0"
                  class="service-booking-section"
                >
                  <p class="booking-instruction" :class="{ gray: isOwner }">
                    <template v-if="isOwner">
                      Other users will be able to select from the available
                      slots below.
                    </template>
                    <template v-else>
                      Select a slot to send a request to the service provider.
                    </template>
                  </p>
                  <table class="selectable availability-table">
                    <thead>
                      <tr>
                        <th v-for="(day, index) in availableDays" :key="index">
                          {{ day.abbreviatedDay }}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="hour in filteredHours" :key="hour">
                        <td
                          v-for="(day, index) in availableDays"
                          :key="index"
                          @click="
                            !isOwner &&
                              isSlotAvailable(day.fullDay, hour) &&
                              toggleSlot(index, hour)
                          "
                          :class="{
                            'selected-slot': isSlotSelected(index, hour),
                            innerSticker: isSlotSelected(index, hour),
                            accentSelection: isSlotSelected(index, hour),
                            gray:
                              !isSlotAvailable(day.fullDay, hour) || isOwner,
                            clickable:
                              !isOwner && isSlotAvailable(day.fullDay, hour),
                          }"
                        >
                          <strong v-if="isSlotAvailable(day.fullDay, hour)">{{
                            hour
                          }}</strong>
                          <span v-else>{{ hour }}</span>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
            <!-- AUDIO ITEM: Cover image with audio player at bottom -->
            <div v-else-if="isAudioItem" class="audio-slideshow-container">
              <!-- Cover image for audio (or placeholder) -->
              <img
                v-if="audioCoverImage"
                :src="audioCoverImage"
                :alt="imageAlt"
                class="slideshow-image audio-cover"
                @load="onMediaLoad"
              />
              <div v-else class="audio-placeholder">
                <svg
                  class="audio-placeholder-icon"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path
                    d="M12 3v10.55c-.59-.34-1.27-.55-2-.55-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4V7h4V3h-6z"
                    fill="currentColor"
                  />
                </svg>
              </div>
              <!-- Audio player at bottom -->
              <div class="audio-slideshow-player">
                <audio
                  ref="audioRef"
                  :src="audioUrl"
                  controls
                  preload="metadata"
                  class="slideshow-audio-element"
                ></audio>
              </div>
            </div>
            <!-- VIDEO ITEM -->
            <template v-else-if="isCurrentMediaVideo">
              <!-- Thumbnail placeholder for video (shows while loading) with spinner on top -->
              <div
                v-if="videoPoster && isLoading"
                class="video-loading-container"
              >
                <img
                  :src="videoPoster"
                  alt="Loading video..."
                  class="slideshow-image video-poster-fallback"
                />
                <!-- Spinner on top of thumbnail -->
                <div class="slideshow-loading">
                  <div class="slideshow-spinner"></div>
                </div>
              </div>
              <!-- Video element (always present to load) -->
              <video
                ref="videoRef"
                :src="currentMediaUrl"
                :poster="videoPoster"
                class="slideshow-image"
                :class="{ 'video-loading': isLoading }"
                controls
                playsinline
                @loadeddata="onVideoLoaded"
                @play="onVideoPlay"
                @pause="onVideoPause"
                @error="onMediaLoad"
              ></video>
              <!-- Play button overlay when video is paused -->
              <div
                v-if="isVideoPaused && !isLoading"
                class="video-play-overlay"
                @click="playVideo"
              >
                <div class="play-button">
                  <svg viewBox="0 0 24 24" class="play-icon">
                    <path d="M8 5v14l11-7z" fill="currentColor" />
                  </svg>
                </div>
              </div>
            </template>
            <!-- Image element -->
            <img
              v-else-if="!isAudioItem"
              :src="currentMediaUrl"
              :alt="imageAlt"
              class="slideshow-image"
              @load="onMediaLoad"
            />
            <!-- Loading indicator for images only (skip text-only items and posts) -->
            <div
              v-if="
                isLoading &&
                !isCurrentMediaVideo &&
                !isAudioItem &&
                !isTextOnlyItem &&
                !isPostItem
              "
              class="slideshow-loading"
            >
              <div class="slideshow-spinner"></div>
            </div>
          </div>

          <!-- Per-image caption (from imageCaptions array) shown under media -->
          <div
            v-if="
              currentImageCaption &&
              !isArticlePresentation &&
              !isStoryPresentation &&
              !isGalleryPresentation
            "
            class="slideshow-image-caption"
          >
            <LinkifiedText :text="currentImageCaption" />
          </div>


        </div>
        </transition>

        <!-- Title at top center (unified for ALL item types) -->
        <!-- When title-only post, center title vertically and horizontally -->
        <div
          v-if="displayTitle && !isArticlePresentation && !isNFTType"
          class="slideshow-title-top"
          :class="{ 'title-only-centered': isTitleOnlyPost }"
        >
          <span class="slideshow-title-text">{{ displayTitle }}</span>
        </div>

        <!-- Image counter (under title) -->
        <div v-if="showNavigationCounter" class="slideshow-counter">
          {{ currentIndex + 1 }} / {{ totalMultiSlides }}
        </div>

        <!-- Caption below media (for media items with caption) -->
        <div
          v-if="
            displayDescription &&
            !isPosterItem &&
            !isTweetItem &&
            !isPostItem &&
            !isProductItem &&
            !isServiceItem &&
            !isDocumentItem &&
            !isEmbedItem &&
            !isNFTType &&
            !isStoryPresentation &&
            !isGalleryPresentation
          "
          class="slideshow-caption-bottom"
        >
          <LinkifiedText :text="displayDescription" />
        </div>

        <!-- Author identity and Follow (bottom left) -->
        <div
          class="slideshow-owner-avatar"
          v-if="avatarData && avatarData.uid"
        >
          <a
            v-if="authorProfileIdentifier"
            class="slideshow-owner-profile"
            :href="authorProfileHref"
            :aria-label="`View ${avatarData.displayName || 'author'} profile`"
            @click.stop.prevent="goToUserProfile"
          >
            <Avatar
              :user="avatarData"
              size="small"
              :emphasis="true"
              decorative
            />
          </a>
          <span v-else class="slideshow-owner-identity">
            <Avatar :user="avatarData" size="small" :emphasis="true" />
          </span>
          <div
            v-if="isFollowUiEnabled && !isOwner && authorFollowTargetId"
            class="slideshow-follow-action"
          >
            <FollowControl
              ref="authorIdentityFollowControl"
              :following="isFollowingAuthor"
              :loading="isAuthorFollowLoading"
              target-type="author"
              :target-label="avatarData.displayName"
              variant="identity"
              @toggle="requestAuthorFollowToggle"
            />
            <FollowDigestDisclosure
              :visible="authorFollowDisclosureOpen"
              :initial-cadence="selectedAuthorFollowCadence"
              :loading="isAuthorFollowLoading"
              :error-message="authorFollowError"
              :return-focus-target="authorIdentityFollowControl"
              @confirm="confirmAuthorFollow"
              @without-email="followAuthorWithoutEmail"
              @cancel="cancelAuthorFollowDisclosure"
            />
          </div>
          <Transition name="slide-fade">
            <p
              v-if="
                isFollowUiEnabled &&
                (authorFollowError || authorFollowStatus) &&
                !authorFollowDisclosureOpen
              "
              class="slideshow-follow-feedback nomargintopbottom"
              :role="authorFollowError ? 'alert' : 'status'"
              aria-live="polite"
            >
              {{ authorFollowError || authorFollowStatus }}
            </p>
          </Transition>
        </div>

        <!-- Actions Bar (right side) -->
        <div class="slideshow-actions-bar" v-if="currentUserUid">
          <!-- Previous post -->
          <button
            type="button"
            class="slideshow-action-button nav-button"
            @click.stop="navigateToPrevPost"
            :disabled="!canNavigatePrevious"
            aria-label="Previous post"
          >
            <svg class="compactImg" aria-hidden="true">
              <use href="@/assets/icons/iconset.svg#arrowup_compact"></use>
            </svg>
          </button>

          <div class="nav-separator"></div>

          <!-- Fullscreen current Story/Gallery media -->
          <button
            v-if="fullscreenMediaItem"
            type="button"
            class="slideshow-action-button"
            @click.stop="openMediaFullscreen(fullscreenMediaItem)"
            aria-label="View media full screen"
          >
            <svg class="compactImg" aria-hidden="true">
              <use href="@/assets/icons/iconset.svg#fullscreen_compact"></use>
            </svg>
            <span class="button-text">Full screen</span>
          </button>

          <!-- Edit Button (for owners, not shown for NFTs or minted items) -->
          <UnitButton
            v-if="isOwner && item && item.id && item.type !== 'nft' && !isItemMinted"
            type="button"
            variant="secondary"
            size="small"
            class="slideshow-action-button"
            @click.stop="$emit('edit', item)"
            aria-label="Edit"
          >
            <template #leading>
              <svg class="compactImg" aria-hidden="true">
                <use href="@/assets/icons/iconset.svg#edit_compact"></use>
              </svg>
            </template>
            <span class="button-text">Edit</span>
          </UnitButton>

          <!-- Mint NFT Button (for media owners only, shown when not yet minted) -->
          <UnitButton
            v-if="isOwner && isMintableType && item && item.id && !isItemMinted"
            type="button"
            variant="secondary"
            size="small"
            class="slideshow-action-button"
            @click.stop="$emit('mint', item)"
            aria-label="Mint NFT"
          >
            <template #leading>
              <svg class="compactImg" aria-hidden="true">
                <use href="@/assets/icons/iconset.svg#nft_compact"></use>
              </svg>
            </template>
            <span class="button-text">Mint</span>
          </UnitButton>

          <!-- NFT Listing Actions (for NFT owners) -->
          <!-- List for Sale -->
          <UnitButton
            v-if="isOwner && isNFTType && nftListingStatus && !nftListingStatus.isListed"
            type="button"
            variant="secondary"
            size="small"
            class="slideshow-action-button"
            @click.stop="handleOverlayListNFT"
            aria-label="List for Sale"
          >
            <template #leading>
              <svg class="compactImg" aria-hidden="true">
                <use href="@/assets/icons/iconset.svg#megaphone_compact"></use>
              </svg>
            </template>
            <span class="button-text">List</span>
          </UnitButton>
          <!-- Edit Listing -->
          <UnitButton
            v-if="isOwner && isNFTType && nftListingStatus && nftListingStatus.isListed"
            type="button"
            variant="secondary"
            size="small"
            class="slideshow-action-button"
            @click.stop="handleOverlayEditListing"
            aria-label="Edit Listing"
          >
            <template #leading>
              <svg class="compactImg" aria-hidden="true">
                <use href="@/assets/icons/iconset.svg#edit_compact"></use>
              </svg>
            </template>
            <span class="button-text">Edit</span>
          </UnitButton>
          <!-- Unlist -->
          <UnitButton
            v-if="isOwner && isNFTType && nftListingStatus && nftListingStatus.isListed"
            type="button"
            variant="secondary"
            size="small"
            class="slideshow-action-button"
            @click.stop="handleOverlayDelistNFT"
            aria-label="Unlist"
          >
            <template #leading>
              <svg class="compactImg" aria-hidden="true">
                <use href="@/assets/icons/iconset.svg#unpost_unlist"></use>
              </svg>
            </template>
            <span class="button-text">Unlist</span>
          </UnitButton>

          <!-- Share Button with dropdown -->
          <div class="share-dropdown-wrapper" v-click-outside="() => { shareMenuOpen = false; }">
            <button
              type="button"
              class="slideshow-action-button"
              @click.stop="shareMenuOpen = !shareMenuOpen; favouritesMenuOpen = false"
              aria-label="Share"
            >
              <svg class="compactImg" aria-hidden="true">
                <use href="@/assets/icons/iconset.svg#share_compact"></use>
              </svg>
              <span class="button-text">Share</span>
            </button>
            <transition name="slide-fade">
              <div v-if="shareMenuOpen" class="share-dropdown-menu">
                <div class="actions-menu">
                  <div class="menu-item" @click.stop="handleShare(); shareMenuOpen = false;">
                    <svg class="tinyImg"><use href="@/assets/icons/iconset.svg#link-compact"></use></svg>
                    Copy link
                  </div>
                  <div
                    v-if="!isAuthorityPresentation"
                    class="menu-item"
                    @click.stop="handleShareToMessages(); shareMenuOpen = false;"
                  >
                    <svg class="tinyImg"><use href="@/assets/icons/iconset.svg#message_compact"></use></svg>
                    Share in Messages
                  </div>
                </div>
              </div>
            </transition>
          </div>

          <!-- Favourites Button with dropdown -->
          <div
            v-if="isWishlistFeatureEnabled && !isAuthorityPresentation"
            class="share-dropdown-wrapper"
            v-click-outside="() => { favouritesMenuOpen = false; }"
          >
            <button
              type="button"
              class="slideshow-action-button"
              :class="{ active: isWishlisted }"
              @click.stop="favouritesMenuOpen = !favouritesMenuOpen; shareMenuOpen = false"
              aria-label="Favourites"
            >
              <svg class="compactImg" aria-hidden="true">
                <use
                  v-if="isWishlisted"
                  href="@/assets/icons/iconset.svg#star_filled_compact"
                ></use>
                <use v-else href="@/assets/icons/iconset.svg#star_compact"></use>
              </svg>
              <span class="button-text">Favourite</span>
            </button>
            <transition name="slide-fade">
              <div v-if="favouritesMenuOpen" class="share-dropdown-menu">
                <div class="actions-menu">
                  <div class="menu-item" @click.stop="toggleWishlist(); favouritesMenuOpen = false;">
                    <svg class="tinyImg">
                      <use
                        v-if="isWishlisted"
                        href="@/assets/icons/iconset.svg#star_filled_compact"
                      ></use>
                      <use v-else href="@/assets/icons/iconset.svg#star_compact"></use>
                    </svg>
                    {{ isWishlisted ? 'Remove from Favourites' : 'Add to Favourites' }}
                  </div>
                </div>
              </div>
            </transition>
          </div>

          <!-- Clap (not for owners, not for wallet NFTs with 0x owner) -->
          <ClapSimple
            v-if="
              !isOwner &&
              item &&
              (item.owner || resolveClapPlacement(item)) &&
              !String(item.owner || '').startsWith('0x')
            "
            show-label
            :placement="resolveClapPlacement(item)"
            :receiverUid="item.owner || null"
          />

          <!-- Message Button (not for owners, not for wallet NFTs with 0x owner) -->
          <UnitButton
            v-if="!isOwner && item && item.owner && !item.owner.startsWith('0x')"
            type="button"
            variant="secondary"
            size="small"
            class="slideshow-action-button"
            @click.stop="handleMessage"
            aria-label="Send message"
          >
            <template #leading>
              <svg class="compactImg" aria-hidden="true">
                <use
                  href="@/assets/icons/iconset.svg#message_compact"
                ></use>
              </svg>
            </template>
            <span class="button-text">Message</span>
          </UnitButton>

          <div class="nav-separator"></div>

          <!-- Next post -->
          <button
            type="button"
            class="slideshow-action-button nav-button"
            @click.stop="navigateToNextPost"
            :disabled="!canNavigateNext"
            aria-label="Next post"
          >
            <svg class="compactImg" aria-hidden="true">
              <use href="@/assets/icons/iconset.svg#arrowdown_compact"></use>
            </svg>
          </button>
        </div>

        <!-- Guest Actions Bar (when not logged in) -->
        <div class="slideshow-actions-bar" v-if="!currentUserUid">
          <button
            type="button"
            class="slideshow-action-button nav-button"
            @click.stop="navigateToPrevPost"
            :disabled="!canNavigatePrevious"
            aria-label="Previous post"
          >
            <svg class="compactImg" aria-hidden="true">
              <use href="@/assets/icons/iconset.svg#arrowup_compact"></use>
            </svg>
          </button>

          <div class="nav-separator"></div>

          <!-- Fullscreen current Story/Gallery media -->
          <button
            v-if="fullscreenMediaItem"
            type="button"
            class="slideshow-action-button"
            @click.stop="openMediaFullscreen(fullscreenMediaItem)"
            aria-label="View media full screen"
          >
            <svg class="compactImg" aria-hidden="true">
              <use href="@/assets/icons/iconset.svg#fullscreen_compact"></use>
            </svg>
            <span class="button-text">Full screen</span>
          </button>

          <!-- Copy link (works without auth) -->
          <UnitButton
            type="button"
            variant="secondary"
            size="small"
            class="slideshow-action-button"
            @click.stop="handleShare"
            aria-label="Copy link"
          >
            <template #leading>
              <svg class="compactImg" aria-hidden="true">
                <use href="@/assets/icons/iconset.svg#share_compact"></use>
              </svg>
            </template>
            <span class="button-text">Share</span>
          </UnitButton>

          <!-- Sign in CTA -->
          <UnitButton
            type="button"
            variant="secondary"
            size="small"
            class="slideshow-action-button"
            @click.stop="openLoginModal"
            aria-label="Sign in"
          >
            <template #leading>
              <svg class="compactImg" aria-hidden="true">
                <use href="@/assets/icons/iconset.svg#signIn-compact"></use>
              </svg>
            </template>
            <span class="button-text">Sign in</span>
          </UnitButton>

          <div class="nav-separator"></div>

          <button
            type="button"
            class="slideshow-action-button nav-button"
            @click.stop="navigateToNextPost"
            :disabled="!canNavigateNext"
            aria-label="Next post"
          >
            <svg class="compactImg" aria-hidden="true">
              <use href="@/assets/icons/iconset.svg#arrowdown_compact"></use>
            </svg>
          </button>
        </div>

        <!-- Minimized bar: avatar + thumbnail (image triggers expand) -->
        <div
          v-if="isMinimized"
          class="slideshow-minimized-bar"
          @click.stop
        >
          <!-- Avatar in minimized bar -->
          <a
            class="slideshow-minimized-avatar"
            v-if="avatarData && avatarData.uid && authorProfileIdentifier"
            :href="authorProfileHref"
            :aria-label="`View ${avatarData.displayName || 'author'} profile`"
            @click.stop.prevent="goToUserProfile"
          >
            <Avatar
              :user="avatarData"
              size="small"
              :emphasis="true"
              decorative
            />
          </a>
          <span
            v-else-if="avatarData && avatarData.uid"
            class="slideshow-minimized-avatar slideshow-minimized-avatar--static"
          >
            <Avatar :user="avatarData" size="small" :emphasis="true" />
          </span>
          <!-- Media thumbnail — click to expand back to overlay -->
          <div
            class="slideshow-minimized-thumb"
            v-if="minimizedThumbnail"
            @click.stop="expandOverlay"
          >
            <img
              :src="minimizedThumbnail"
              alt=""
              class="slideshow-minimized-thumb-img"
            />
          </div>
          <!-- Fallback: placeholder — click to expand back to overlay -->
          <div
            class="slideshow-minimized-thumb slideshow-minimized-thumb-fallback"
            v-else
            @click.stop="expandOverlay"
          >
            <svg class="compactImg" aria-hidden="true">
              <use href="@/assets/icons/iconset.svg#image-compact"></use>
            </svg>
          </div>
        </div>
      </div>
    </transition>
    <!-- Floating player conflict dialog — rendered outside slideshow stacking context -->
    <Modal
      :visible="conflictDialogVisible"
      title="Something is already playing"
      width="320px"
      :close-on-backdrop="false"
      :show-close-button="false"
      @close="conflictDialogVisible = false"
    >
      <template #footer>
        <UnitButton
          variant="secondary"
          size="small"
          @click="conflictAddToQueue"
        >
          Add to queue
        </UnitButton>
        <UnitButton
          variant="secondary"
          size="small"
          @click="conflictPauseAndPlay"
        >
          Pause &amp; play this
        </UnitButton>
      </template>
    </Modal>
    <!-- Ultra Overlay (fullscreen single-media viewer) -->
    <UltraOverlay
      :visible="showUltraOverlay"
      :url="ultraMediaUrl"
      :poster="ultraMediaPoster"
      :mediaType="ultraMediaType"
      @close="closeUltraOverlay"
    />
    <!-- List NFT Overlay Modal -->
    <ListNFTOverlay
      :visible="showListNFTOverlay"
      :nft="item"
      :is-edit="isEditingListing"
      :existing-price="existingListingPrice"
      @close="closeListNFTOverlay"
      @listed="handleOverlayNFTListed"
      @updated="handleOverlayNFTListingUpdated"
    />
  </teleport>
</template>

<script>
import { ref, computed, watch, nextTick, onMounted, onBeforeUnmount } from "vue";
import { useStore } from "vuex";
import { useRouter } from "vue-router";
import {
  doc,
  collection,
  getDocs,
  setDoc,
  deleteDoc,
  getDoc,
  serverTimestamp,
} from "@/services/legacyBrowserPersistenceBoundary";
import { auth } from "@/firebase";
import { toast } from "vue3-toastify";
import { getConvertedPrice } from "@/utils/priceUtils";
import { smartTitleCase } from "@/utils/smartTitleCase";
import { getMarketplaceAddress } from "@/config/nft";
import iconsetPath from "@/assets/icons/iconset.svg";
import ClapSimple from "@/components/DashboardComponents/EcosystemComponents/ProductListComponents/ClapSimple.vue";
import { sanitizeEmbedHtml, isVerticalEmbed, extractYouTubeId, injectYouTubeApiParams } from "@/utils/postComposerUtils";
import { ipfsToHttp } from "@/utils/ipfsUtils";
import { truncateText } from "@/utils/resolveCardText";
import { resolveClapPlacement } from "@/utils/transferClapMoney";
import {
  PRESENTATION_MODES,
  getVisualMedia,
  resolvePresentationMode,
  shouldCoverStoryMedia,
} from "@/utils/postPresentation";
import Avatar from "@/components/GenericComponents/Avatar.vue";
import LinkifiedText from "@/components/GenericComponents/LinkifiedText.vue";
import OrganicMediaGrid from "@/components/GenericComponents/OrganicMediaGrid.vue";
import MediaZoomOverlay from "@/components/GenericComponents/MediaZoomOverlay.vue";
import UltraOverlay from "@/components/GenericComponents/UltraOverlay.vue";
import ItemDetailPanel from "@/components/GenericComponents/ItemDetailPanel.vue";
import AuthorityListingPurchase from "@/components/DashboardComponents/EcosystemComponents/AuthorityListingPurchase.vue";
import { itemMediaUrlResolver } from "@/services/itemMediaUrlResolver";
import ListNFTOverlay from "@/components/WalletComponents/ListNFTOverlay.vue";
import Modal from "@/components/GenericComponents/Modal.vue";
import UnitButton from "@/components/GenericComponents/UnitButton.vue";
import FollowControl from "@/components/GenericComponents/FollowControl.vue";
import FollowDigestDisclosure from "@/components/GenericComponents/FollowDigestDisclosure.vue";
import { useNFTListing } from "@/composables/useNFTListing";
import { useContentSubscription } from "@/composables/useContentSubscription";
import {
  isFollowUiEnabled,
  isWishlistFeatureEnabled,
} from "@/config/runtimeConfig";
import {
  acquireDocumentScrollLock,
  canLeaveScrollableContent,
  classifyOverlaySwipe,
} from "@/utils/overlayNavigation";
import {
  itemAuthorProfileIdentifier,
  userProfileRoute,
} from "@/utils/profilePublicIdentifier";

export default {
  name: "ImageSlideshowOverlay",
  components: {
    ClapSimple,
    Avatar,
    LinkifiedText,
    OrganicMediaGrid,
    MediaZoomOverlay,
    UltraOverlay,
    ItemDetailPanel,
    AuthorityListingPurchase,
    ListNFTOverlay,
    Modal,
    UnitButton,
    FollowControl,
    FollowDigestDisclosure,
  },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    item: {
      type: Object,
      default: null,
    },
    startIndex: {
      type: Number,
      default: 0,
    },
    canNavigatePrevious: {
      type: Boolean,
      default: false,
    },
    canNavigateNext: {
      type: Boolean,
      default: false,
    },
    itemNavigationLoading: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["close", "edit", "delete", "mint", "message", "favouritesUpdated", "next", "prev", "nft-listed", "nft-delisted", "purchase-settled"],
  setup(props, { emit }) {
    const SPOTIFY_URL_RE = /open\.spotify\.com\/(?:intl-[^/]+\/)?(?:track|album|playlist|artist|show|episode)\//i;
    const SPOTIFY_EMBED_RE = /open\.spotify\.com\/embed\/(?:intl-[^/]+\/)?(track|album|playlist|artist|show|episode)\/([\w-]+)/i;

    const normalizeExternalUrl = (raw) => {
      const value = (raw || "").trim();
      if (!value) return "";
      if (/^https?:\/\//i.test(value)) return value;
      if (/^(?:open\.)?spotify\.com\//i.test(value) || /^www\./i.test(value)) {
        return `https://${value}`;
      }
      return "";
    };

    const deriveSpotifyOpenUrl = (html) => {
      const match = (html || "").match(SPOTIFY_EMBED_RE);
      if (!match) return "";
      return `https://open.spotify.com/${match[1]}/${match[2]}`;
    };

    const store = useStore();
    const router = useRouter();
    const overlayRef = ref(null);
    let releaseOverlayScrollLock = null;
    const currentIndex = ref(0);
    const isLoading = ref(true);
    const isWishlisted = ref(false);
    const isMobileViewport = ref(false);
    let mobileViewportQuery = null;

    const syncMobileViewport = (event) => {
      isMobileViewport.value = !!event?.matches;
    };

    // Share dropdown state
    const shareMenuOpen = ref(false);

    // Favourites dropdown state
    const favouritesMenuOpen = ref(false);

    // Embed playback state (YouTube / SoundCloud postMessage tracking)
    const embedIsPlaying = ref(false);
    const embedCurrentTime = ref(0);

    // YouTube postMessage handler — tracks play state + currentTime
    const handleYouTubeMessage = (event) => {
      if (!event.origin || (!/youtube\.com$/i.test(event.origin) && !/youtube-nocookie\.com$/i.test(event.origin))) return;
      let data;
      try {
        data = typeof event.data === "string" ? JSON.parse(event.data) : event.data;
      } catch { return; }
      if (!data) return;
      // State change: 1=playing, 2=paused, 0=ended, -1=unstarted
      if (data.event === "onStateChange" || data.info?.playerState !== undefined) {
        const state = data.info?.playerState ?? data.info;
        if (state === 1) {
          embedIsPlaying.value = true;
        } else if (state === 0 || state === 2 || state === -1) {
          embedIsPlaying.value = false;
        }
      }
      // currentTime delivery
      if (data.info?.currentTime !== undefined) {
        embedCurrentTime.value = data.info.currentTime;
      }
    };

    // SoundCloud postMessage handler — tracks play/pause
    const handleSoundCloudMessage = (event) => {
      if (!event.origin || !/soundcloud\.com$/i.test(event.origin)) return;
      let data;
      try {
        data = typeof event.data === "string" ? JSON.parse(event.data) : event.data;
      } catch { return; }
      if (!data) return;
      if (data.method === "play") embedIsPlaying.value = true;
      if (data.method === "pause" || data.method === "finish") embedIsPlaying.value = false;
    };

    // Combined message handler for all embed postMessage events
    const handlePostMessage = (event) => {
      handleYouTubeMessage(event);
      handleSoundCloudMessage(event);
    };

    // If overlay embed starts playing while floating player is active on a DIFFERENT item,
    // pause the overlay embed and show a conflict resolution dialog.
    const conflictDialogVisible = ref(false);

    const pauseOverlayEmbed = () => {
      const iframe = document.querySelector(".embed-slideshow-player iframe");
      if (!iframe) return;
      const src = iframe.src || "";
      if (/youtube/i.test(src)) {
        try { iframe.contentWindow.postMessage(JSON.stringify({ event: "command", func: "pauseVideo", args: [] }), "*"); } catch {}
      } else if (/soundcloud/i.test(src)) {
        try { iframe.contentWindow.postMessage(JSON.stringify({ method: "pause" }), "*"); } catch {}
      }
    };

    const resumeOverlayEmbed = () => {
      const iframe = document.querySelector(".embed-slideshow-player iframe");
      if (!iframe) return;
      const src = iframe.src || "";
      if (/youtube/i.test(src)) {
        try { iframe.contentWindow.postMessage(JSON.stringify({ event: "command", func: "playVideo", args: [] }), "*"); } catch {}
      } else if (/soundcloud/i.test(src)) {
        try { iframe.contentWindow.postMessage(JSON.stringify({ method: "play" }), "*"); } catch {}
      }
    };



    // Conflict dialog: add this overlay item to the playlist (keep FP playing)
    const conflictAddToQueue = () => {
      store.dispatch("UIState/addItemToFloatingPlayerQueue", props.item);
      conflictDialogVisible.value = false;
      // FP keeps playing, overlay stays open — user can watch/read the item
    };

    // Conflict dialog: pause the floating player & play this one
    const conflictPauseAndPlay = () => {
      store.dispatch("UIState/pauseFloatingPlayer");
      conflictDialogVisible.value = false;
      // Overlay is now free to play — user starts it or it autoplays
    };

    // Setup YouTube iframe API listener after embed renders
    const setupYouTubePostMessage = () => {
      const iframe = document.querySelector(".embed-slideshow-player iframe");
      if (!iframe || !/youtube/i.test(iframe.src || "")) return;
      try {
        iframe.contentWindow.postMessage(JSON.stringify({ event: "listening" }), "*");
        iframe.contentWindow.postMessage(JSON.stringify({ event: "command", func: "addEventListener", args: ["onStateChange"] }), "*");
      } catch { /* cross-origin — will still receive messages via enablejsapi */ }
    };

    // Setup SoundCloud widget API listener after embed renders
    const setupSoundCloudPostMessage = () => {
      const iframe = document.querySelector(".embed-slideshow-player iframe");
      if (!iframe || !/soundcloud/i.test(iframe.src || "")) return;
      try {
        iframe.contentWindow.postMessage(JSON.stringify({ method: "addEventListener", value: "play" }), "*");
        iframe.contentWindow.postMessage(JSON.stringify({ method: "addEventListener", value: "pause" }), "*");
        iframe.contentWindow.postMessage(JSON.stringify({ method: "addEventListener", value: "finish" }), "*");
      } catch { /* cross-origin */ }
    };

    // Close handler — emits current playback state for handoff decisions
    const handleClose = () => {
      const audioIsPlaying = !!(
        audioRef.value &&
        !audioRef.value.paused &&
        !audioRef.value.ended
      );
      const audioCurrentTime = audioRef.value?.currentTime || 0;
      const mediaIsPlaying = embedIsPlaying.value || audioIsPlaying;
      const mediaCurrentTime = embedIsPlaying.value
        ? embedCurrentTime.value
        : audioCurrentTime;

      console.log("[Overlay] handleClose", {
        embedIsPlaying: embedIsPlaying.value,
        embedCurrentTime: embedCurrentTime.value,
        audioIsPlaying,
        audioCurrentTime,
        mediaIsPlaying,
        mediaCurrentTime,
      });

      emit("close", {
        embedIsPlaying: embedIsPlaying.value,
        embedCurrentTime: embedCurrentTime.value,
        audioIsPlaying,
        audioCurrentTime,
        mediaIsPlaying,
        mediaCurrentTime,
      });
    };

    // Register/cleanup postMessage listener
    onMounted(() => {
      window.addEventListener("message", handlePostMessage);
      if (typeof window.matchMedia === "function") {
        mobileViewportQuery = window.matchMedia("(max-width: 590px)");
        syncMobileViewport(mobileViewportQuery);
        if (typeof mobileViewportQuery.addEventListener === "function") {
          mobileViewportQuery.addEventListener("change", syncMobileViewport);
        } else {
          mobileViewportQuery.addListener?.(syncMobileViewport);
        }
      }
    });
    onBeforeUnmount(() => {
      window.removeEventListener("message", handlePostMessage);
      releaseOverlayScrollLock?.();
      releaseOverlayScrollLock = null;
      if (itemNavigationDirectionTimer) {
        clearTimeout(itemNavigationDirectionTimer);
      }
      if (wheelResetTimer) {
        clearTimeout(wheelResetTimer);
      }
      if (typeof mobileViewportQuery?.removeEventListener === "function") {
        mobileViewportQuery.removeEventListener("change", syncMobileViewport);
      } else {
        mobileViewportQuery?.removeListener?.(syncMobileViewport);
      }
    });

    // Delete confirmation state
    const deleteConfirmPending = ref(false);
    let deleteConfirmTimer = null;

    const handleDeleteClick = () => {
      if (deleteConfirmPending.value) {
        // Second click — confirm
        deleteConfirmPending.value = false;
        if (deleteConfirmTimer) { clearTimeout(deleteConfirmTimer); deleteConfirmTimer = null; }
        emit('delete', props.item);
      } else {
        // First click — enter confirm phase
        deleteConfirmPending.value = true;
        deleteConfirmTimer = setTimeout(() => {
          deleteConfirmPending.value = false;
          deleteConfirmTimer = null;
        }, 3000);
      }
    };

    // Buy confirmation state (same pattern as delete)
    const buyConfirmPending = ref(false);
    let buyConfirmTimer = null;

    const handleBuyOfferClick = async () => {
      if (!props.item?.listingPrice) {
        // No price = Make Offer — no confirmation needed
        await handleMakeOffer();
        return;
      }
      if (buyConfirmPending.value) {
        // Second click — confirm buy
        buyConfirmPending.value = false;
        if (buyConfirmTimer) { clearTimeout(buyConfirmTimer); buyConfirmTimer = null; }
        await handleMakeOffer();
      } else {
        // First click — enter confirm phase
        buyConfirmPending.value = true;
        buyConfirmTimer = setTimeout(() => {
          buyConfirmPending.value = false;
          buyConfirmTimer = null;
        }, 3000);
      }
    };

    // Minimize / expand state
    const isMinimized = ref(false);

    const minimizeOverlay = () => {
      isMinimized.value = true;
    };

    const goToUserProfile = () => {
      const destination = userProfileRoute(authorProfileIdentifier.value);
      if (!destination) return;
      // Minimize the overlay first, then navigate to the user profile
      minimizeOverlay();
      router.push(destination);
    };

    const expandOverlay = () => {
      isMinimized.value = false;
    };

    watch(
      [() => props.visible, isMinimized],
      ([visible, minimized]) => {
        const shouldLock = visible && !minimized;
        if (shouldLock && !releaseOverlayScrollLock) {
          releaseOverlayScrollLock = acquireDocumentScrollLock();
        } else if (!shouldLock && releaseOverlayScrollLock) {
          releaseOverlayScrollLock();
          releaseOverlayScrollLock = null;
        }
      },
      { immediate: true },
    );

    // Price conversion state
    const convertedPrice = ref(null);
    const displayCoinSymbol = ref("");

    // Current user info - use auth.currentUser like ProductView does
    const currentUserUid = computed(
      () =>
        store.getters.userCredentials?.data?.uid ||
        store.getters.userCredentials?.uid ||
        auth.currentUser?.uid ||
        null,
    );
    const currentOrgUID = computed(() => store.getters.currentOrgUID);
    const isAuthorityPresentation = computed(
      () => props.item?._authorityPresentation === true,
    );

    // Check if current user owns this item
    const isOwner = computed(() => {
      if (!props.item || !currentUserUid.value) return false;
      // Wallet NFTs set this flag since owner is a 0x address, not a Firebase UID
      if (props.item.isOwnedByCurrentUser) return true;
      return props.item.owner === currentUserUid.value;
    });

    // Open login modal (for logged-out users)
    const openLoginModal = () => {
      store.dispatch("toggleLoginModal");
    };

    // Auth guard for logged-out users — shows toast + opens login modal
    const requireAuth = (actionLabel) => {
      if (currentUserUid.value) return true;
      toast.info(`Log in to ${actionLabel}`, { autoClose: 3000 });
      store.dispatch("toggleLoginModalWithReason", actionLabel);
      return false;
    };

    // Avatar data - shows owner data OR current user data if they own the item
    const avatarData = ref(null);
    const authorIdentityFollowControl = ref(null);
    let unsubscribeAvatarData = null;

    // Author profile links use the canonical public identifier only. Without
    // it the author avatar is shown unlinked (never a UID link).
    const authorProfileIdentifier = computed(() =>
      avatarData.value?.uid
        ? itemAuthorProfileIdentifier({
            item: props.item,
            isOwner: isOwner.value,
            currentUserUid: currentUserUid.value,
            ownPublicIdentifier:
              store.getters.ownPublicIdentifierForUid?.(currentUserUid.value) ||
              null,
          })
        : null,
    );
    const authorProfileHref = computed(() => {
      const destination = userProfileRoute(authorProfileIdentifier.value);
      return destination ? router.resolve(destination).href : "#";
    });

    const authorFollowTargetId = computed(() => {
      const uid = avatarData.value?.uid;
      return typeof uid === "string" && !uid.startsWith("0x") ? uid : "";
    });
    const {
      following: isFollowingAuthor,
      isLoading: isAuthorFollowLoading,
      disclosureOpen: authorFollowDisclosureOpen,
      selectedCadence: selectedAuthorFollowCadence,
      errorMessage: authorFollowError,
      statusMessage: authorFollowStatus,
      requestToggle: requestAuthorFollowToggle,
      confirmDigestSetup: confirmAuthorDigestSetup,
      followWithoutEmail: submitAuthorFollowWithoutEmail,
      cancelDigestSetup: cancelAuthorFollowDisclosure,
    } = useContentSubscription({
      enabled: isFollowUiEnabled,
      targetType: "user",
      targetId: authorFollowTargetId,
      currentUserUid,
      requireAuth: () => requireAuth("follow this author"),
    });

    const confirmAuthorFollow = (nextCadence) => {
      selectedAuthorFollowCadence.value = nextCadence;
      return confirmAuthorDigestSetup();
    };

    const followAuthorWithoutEmail = (nextCadence) => {
      selectedAuthorFollowCadence.value = nextCadence;
      return submitAuthorFollowWithoutEmail();
    };

    // Current user data from store (Firestore doc)
    const currentUserData = computed(() => store.getters.user?.data || null);
    // Firebase Auth credentials (has displayName, photoURL, isAnonymous, providerData)
    const currentUserCredentials = computed(
      () => store.getters.userCredentials?.data || null,
    );

    const stopAvatarDataSubscription = () => {
      unsubscribeAvatarData?.();
      unsubscribeAvatarData = null;
    };
    onBeforeUnmount(stopAvatarDataSubscription);

    const getAvatarSeed = () => {
      const ownerUid =
        isOwner.value && currentUserUid.value
          ? currentUserUid.value
          : props.item?.owner;
      if (!ownerUid) return null;

      const itemHints = {
        displayName:
          props.item?.ownerDisplayName ||
          props.item?.ownerName ||
          props.item?.creatorName ||
          "",
        email: props.item?.ownerEmail || "",
        isAnonymous: false,
      };

      if (!isOwner.value) {
        return { ...itemHints, uid: ownerUid };
      }

      const creds = currentUserCredentials.value || {};
      const localProfile = currentUserData.value || {};
      return {
        ...itemHints,
        ...creds,
        ...localProfile,
        displayName:
          localProfile.displayName ||
          creds.displayName ||
          itemHints.displayName,
        email: localProfile.email || creds.email || itemHints.email,
        isAnonymous: creds.isAnonymous ?? localProfile.isAnonymous ?? false,
        providerData: creds.providerData || localProfile.providerData || [],
        uid: ownerUid,
      };
    };

    // Keep only the allowlisted item presentation seed. A richer owner profile
    // must come from a bounded public-presentation API.
    const fetchAvatarData = () => {
      stopAvatarDataSubscription();

      if (!props.item?.owner) {
        console.log("[Slideshow] No item owner, avatar cleared");
        avatarData.value = null;
        return;
      }
      // On-chain wallet NFTs use wallet addresses as owner — skip Firestore lookup
      // But if the current user owns it, fall through to show their avatar
      if (isNFTType.value && props.item.owner.startsWith("0x") && !isOwner.value) {
        avatarData.value = null;
        return;
      }

      const seed = getAvatarSeed();
      avatarData.value = seed;
    };

    // === NFT Listing Management ===
    const nftListingStatus = ref(null); // { isListed, price, timelineDocId }
    const showListNFTOverlay = ref(false);
    const isEditingListing = ref(false);
    const existingListingPrice = ref(0);

    const getNFTId = () => {
      if (!props.item) return null;
      const chainId = props.item.chainId || "0x2105";
      const contractAddress = props.item.contractAddress || props.item.nftContractAddress;
      const tokenId = props.item.tokenId || props.item.nftTokenId;
      if (!contractAddress || !tokenId) return null;
      return `nft-${chainId}-${contractAddress}-${tokenId}`;
    };

    const checkNFTListingStatus = async () => {
      if (isAuthorityPresentation.value) {
        nftListingStatus.value = null;
        return;
      }
      if (!isNFTType.value) {
        nftListingStatus.value = null;
        return;
      }
      const nftId = getNFTId();
      if (!nftId) {
        nftListingStatus.value = { isListed: false, price: 0, timelineDocId: null };
        return;
      }
      try {
        const snap = await getDoc(doc(null, "nftListings", nftId));
        if (snap.exists() && snap.data().listingStatus === "active") {
          nftListingStatus.value = {
            isListed: true,
            price: snap.data().listingPrice || 0,
            timelineDocId: snap.data().timelineDocId || null,
          };
        } else {
          nftListingStatus.value = { isListed: false, price: 0, timelineDocId: null };
        }
      } catch {
        nftListingStatus.value = { isListed: false, price: 0, timelineDocId: null };
      }
    };

    const handleOverlayListNFT = () => {
      isEditingListing.value = false;
      existingListingPrice.value = 0;
      showListNFTOverlay.value = true;
    };

    const handleOverlayEditListing = () => {
      isEditingListing.value = true;
      existingListingPrice.value = nftListingStatus.value?.price || 0;
      showListNFTOverlay.value = true;
    };

    const handleOverlayDelistNFT = async () => {
      const nftId = getNFTId();
      if (!nftId) return;

      const nftData = computed(() => props.item);
      const { delistNFT } = useNFTListing({ currentUserUid, nftData });
      const result = await delistNFT(nftId);

      if (result.success) {
        const previousStatus = nftListingStatus.value;
        nftListingStatus.value = { isListed: false, price: 0, timelineDocId: null };

        if (previousStatus?.timelineDocId) {
          window.dispatchEvent(
            new CustomEvent("marketplace:nft-delisted", {
              detail: { timelineDocId: previousStatus.timelineDocId, nftId },
            }),
          );
        }

        emit("nft-delisted", { nftId });
        toast.success(`Removed ${props.item?.name || props.item?.itemName || "NFT"} from marketplace`);
      }
    };

    const handleOverlayNFTListed = (data) => {
      nftListingStatus.value = {
        isListed: true,
        price: data.price,
        timelineDocId: data.timelineDocId,
      };
      window.dispatchEvent(new CustomEvent("marketplace:new-media"));
      showListNFTOverlay.value = false;
      emit("nft-listed", data);
    };

    const handleOverlayNFTListingUpdated = (data) => {
      if (nftListingStatus.value) {
        nftListingStatus.value.price = data.price;
      }
      showListNFTOverlay.value = false;
    };

    const closeListNFTOverlay = () => {
      showListNFTOverlay.value = false;
    };

    // Service booking state
    const availableSlots = ref([]);
    const selectedSlot = ref({ dayIndex: null, hour: null });
    const hours = Array.from(
      { length: 24 },
      (_, i) => `${i.toString().padStart(2, "0")}:00`,
    );

    // Fetch available slots for services
    const fetchAvailableSlots = async () => {
      if (isAuthorityPresentation.value) {
        availableSlots.value = [];
        return;
      }
      if (props.item?.type !== "service" || !props.item?.id) {
        availableSlots.value = [];
        return;
      }
      try {
        const slotsRef = collection(null, "services", props.item.id, "slots");
        const slotsSnapshot = await getDocs(slotsRef);
        availableSlots.value = slotsSnapshot.docs.map((doc) => ({
          day: doc.id,
          slots: doc.data().slots,
        }));
      } catch (error) {
        console.error("Error fetching available slots:", error);
        availableSlots.value = [];
      }
    };

    // Available days computed from slots
    const availableDays = computed(() => {
      const dayOrder = [
        "Monday",
        "Tuesday",
        "Wednesday",
        "Thursday",
        "Friday",
        "Saturday",
        "Sunday",
      ];
      const dayAbbreviations = {
        Monday: "Mon",
        Tuesday: "Tue",
        Wednesday: "Wed",
        Thursday: "Thu",
        Friday: "Fri",
        Saturday: "Sat",
        Sunday: "Sun",
      };
      return availableSlots.value
        .sort((a, b) => dayOrder.indexOf(a.day) - dayOrder.indexOf(b.day))
        .map((slot) => ({
          fullDay: slot.day,
          abbreviatedDay: dayAbbreviations[slot.day],
        }));
    });

    // Filtered hours (only hours that have slots)
    const filteredHours = computed(() => {
      return hours.filter((hour) => {
        return availableSlots.value.some((slot) =>
          slot.slots.some((s) => s.start === hour),
        );
      });
    });

    // Check if a slot is available
    const isSlotAvailable = (fullDay, hour) => {
      const daySlots = availableSlots.value.find(
        (slot) => slot.day === fullDay,
      );
      return daySlots
        ? daySlots.slots.some((slot) => slot.start === hour)
        : false;
    };

    // Check if a slot is selected
    const isSlotSelected = (dayIndex, hour) =>
      selectedSlot.value.dayIndex === dayIndex &&
      selectedSlot.value.hour === hour;

    // Fetch converted price for display
    const fetchConvertedPrice = async () => {
      if (isAuthorityPresentation.value) {
        convertedPrice.value = null;
        displayCoinSymbol.value =
          props.item?.currencySymbol || props.item?.currencyCode || "";
        return;
      }
      // NFT listings use listingPrice in crypto — skip Unit coin conversion
      if (props.item?.type === "nft" || props.item?.isNFTListing) {
        convertedPrice.value = null;
        displayCoinSymbol.value = props.item.currency || props.item.coinSymbol || "";
        return;
      }

      if (!props.item?.price || !props.item?.organisation) {
        convertedPrice.value = null;
        displayCoinSymbol.value = props.item?.coinSymbol || "";
        return;
      }

      // If owner is viewing, show original price
      if (isOwner.value) {
        convertedPrice.value = null;
        displayCoinSymbol.value = props.item.coinSymbol || "";
        return;
      }

      try {
        // Fetch converted price
        const result = await getConvertedPrice(
          props.item,
          props.item.organisation,
          props.item.price,
        );
        convertedPrice.value = result.convertedPrice;
        displayCoinSymbol.value = result.coinSymbol || props.item.coinSymbol;
      } catch (error) {
        console.error("Error fetching converted price:", error);
        convertedPrice.value = null;
        displayCoinSymbol.value = props.item.coinSymbol || "";
      }
    };

    // Generate unique message ID
    const generateMessageId = () =>
      `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

    // Toggle slot selection and open interactive message for services
    const toggleSlot = async (dayIndex, hour) => {
      if (isOwner.value) return; // Owners can't select slots

      if (
        selectedSlot.value.dayIndex === dayIndex &&
        selectedSlot.value.hour === hour
      ) {
        selectedSlot.value = { dayIndex: null, hour: null };
        return;
      }

      selectedSlot.value = { dayIndex, hour };

      const bookingDate = availableDays.value[dayIndex]?.fullDay;
      const bookingTime = hour;

      if (!bookingDate) return;

      try {
        const slotDuration = props.item.availability?.slotDuration || 50;

        // Create interactive message for service booking
        const interactiveMessage = {
          id: generateMessageId(),
          from: currentUserUid.value,
          to: props.item.owner,
          messageBody: `Slot selected: ${bookingDate} at ${bookingTime}`,
          messageTime: Date.now(),
          isInteractive: true,
          interactiveResponse: null,
          interactiveResponseStatus: "pending",
          type: "service",
          productId: props.item.id,
          itemName: props.item.itemName || props.item.title,
          productPrice: parseFloat(props.item.price) || 0,
          productImageUrl:
            props.item.images?.[props.item.thumbnailIndex || 0] ||
            props.item.images?.[0],
          bookingDate,
          bookingTime,
          slotDuration,
        };

        // Dispatch to open interactive messages
        await store.dispatch("UIState/fetchOtherUserData", {
          product: props.item,
        });
        await store.dispatch("UIState/setProductInfo", interactiveMessage);
        await store.dispatch("UIState/setMessagesOpen", true);
        await store.dispatch("UIState/setMessagesMaximised", true);
        await store.dispatch("UIState/setShowInteractiveMessage", true);
      } catch (error) {
        console.error("Error in toggleSlot:", error);
        toast.error("Failed to open booking", { autoClose: 3000 });
      }
    };

    // Handle Make Offer for products - opens interactive message
    const handleMakeOffer = async () => {
      if (!props.item || isOwner.value) return;

      try {
        let convertedPrice, buyerCoinSymbol, sellerCoinSymbol;

        if (props.item.type === "nft" || props.item.isNFTListing) {
          // NFT listings: use crypto listing price directly, skip org conversion
          convertedPrice = parseFloat(props.item.listingPrice) || 0;
          buyerCoinSymbol = props.item.currency || props.item.coinSymbol || "ETH";
          sellerCoinSymbol = buyerCoinSymbol;
        } else {
          // Regular products/services: fetch org details and convert price
          await Promise.all([
            store.dispatch("fetchAndCommitOrgMembersCount", {
              orgUID: currentOrgUID.value,
              context: "buyer",
            }),
            store.dispatch("fetchAndCommitOrgTotalCoins", {
              orgUID: currentOrgUID.value,
              context: "buyer",
            }),
            store.dispatch("fetchAndCommitOrgMembersCount", {
              orgUID: props.item.organisation,
              context: "seller",
            }),
            store.dispatch("fetchAndCommitOrgTotalCoins", {
              orgUID: props.item.organisation,
              context: "seller",
            }),
          ]);

          const result = await getConvertedPrice(
            props.item,
            props.item.organisation,
            props.item.price,
          );
          convertedPrice = parseFloat(result.convertedPrice);
          buyerCoinSymbol = result.coinSymbol;
          sellerCoinSymbol = props.item.coinSymbol || "SellerCoinSymbol";
        }

        // Create interactive message for product offer
        const interactiveMessage = {
          id: generateMessageId(),
          from: currentUserUid.value,
          to: props.item.owner,
          messageBody: `I'm interested in: ${
            props.item.itemName || props.item.title
          }`,
          messageTime: Date.now(),
          isInteractive: true,
          interactiveResponse: null,
          interactiveResponseStatus: "pending",
          type: (props.item.type === "nft" || props.item.isNFTListing) ? "nft" : "product",
          productId: props.item.id,
          itemName: props.item.itemName || props.item.title,
          productPrice: (props.item.type === "nft" || props.item.isNFTListing)
            ? parseFloat(props.item.listingPrice) || 0
            : parseFloat(props.item.price) || 0,
          productImageUrl:
            props.item.images?.[props.item.thumbnailIndex || 0] ||
            props.item.images?.[0],
          convertedPrice: convertedPrice,
          coinSymbol: buyerCoinSymbol,
          buyerCoinSymbol: buyerCoinSymbol,
          sellerCoinSymbol: sellerCoinSymbol,
        };

        // Dispatch to open interactive messages
        await store.dispatch("UIState/fetchOtherUserData", {
          product: props.item,
        });
        await store.dispatch("UIState/setProductInfo", interactiveMessage);
        await store.dispatch("UIState/setMessagesOpen", true);
        await store.dispatch("UIState/setMessagesMaximised", true);
        await store.dispatch("UIState/setShowInteractiveMessage", true);
      } catch (error) {
        console.error("Error in handleMakeOffer:", error);
        toast.error("Failed to open offer", { autoClose: 3000 });
      }
    };

    // Item type detection
    const itemType = computed(() => {
      if (!props.item) return "media";
      if (props.item.type === "product" || props.item.type === "nft" || props.item.isNFTListing) return "product";
      if (props.item.type === "service") return "service";
      return "media";
    });

    // Check if item type is mintable (media types only)
    const isMintableType = computed(() => {
      if (!props.item) return false;
      const mediaType = props.item.mediaType;
      // Only image, video, audio are mintable
      return ["image", "video", "audio"].includes(mediaType);
    });

    // Check if item is already minted
    const isItemMinted = computed(() => {
      if (!props.item) return false;
      return !!props.item.nftTokenId
        || !!props.item.nftContractAddress
        || !!props.item.contractAddress
        || !!props.item.tokenId
        || props.item.onChainStatus === "minted";
    });

    // Block explorer base URLs keyed by chain ID
    const EXPLORER_URLS = {
      "0x2105": "https://basescan.org",
      "0x1": "https://etherscan.io",
      "0x89": "https://polygonscan.com",
      "0xa4b1": "https://arbiscan.io",
      "0xa": "https://optimistic.etherscan.io",
    };
    const EXPLORER_NAMES = {
      "0x2105": "Basescan",
      "0x1": "Etherscan",
      "0x89": "Polygonscan",
      "0xa4b1": "Arbiscan",
      "0xa": "Etherscan (OP)",
    };

    // Derive chain ID from contract address
    const itemChainId = computed(() => {
      if (props.item?.chainId && EXPLORER_URLS[props.item.chainId]) {
        return props.item.chainId;
      }
      const addr = props.item?.contractAddress;
      if (!addr) return null;
      const norm = addr.toLowerCase();
      // Check all known contract addresses across chains
      for (const chainId of ["0x2105", "0x1", "0x89", "0xa4b1", "0xa"]) {
        try {
          const marketAddr = getMarketplaceAddress(chainId);
          if (marketAddr && marketAddr.toLowerCase() === norm) return chainId;
        } catch { /* ignore */ }
      }
      // Default to Base if we can't determine
      return "0x2105";
    });

    // NFT + IPFS links for minted items
    const ipfsLinks = computed(() => {
      if (!props.item || !isItemMinted.value) return [];
      const links = [];
      const i = props.item;
      const contractAddress = i.contractAddress || i.nftContractAddress;
      const tokenId = i.tokenId || i.nftTokenId;
      const chainId = itemChainId.value;
      const explorerBase = EXPLORER_URLS[chainId];
      const explorerName = EXPLORER_NAMES[chainId] || "Explorer";

      // Block explorer: token page (contract + tokenId)
      if (explorerBase && contractAddress && tokenId) {
        links.push({
          label: `View on ${explorerName}`,
          url: `${explorerBase}/nft/${contractAddress}/${tokenId}`,
          isExplorer: true,
        });
      } else if (explorerBase && i.mintTxHash) {
        // Fallback: link to tx if no tokenId
        links.push({
          label: `View on ${explorerName}`,
          url: `${explorerBase}/tx/${i.mintTxHash}`,
          isExplorer: true,
        });
      }

      // IPFS metadata
      if (i.metadataGatewayUrl) {
        links.push({ label: "IPFS Metadata", url: i.metadataGatewayUrl });
      } else if (i.metadataIpfsUri) {
        const normalized = ipfsToHttp(i.metadataIpfsUri) || i.metadataIpfsUri;
        links.push({ label: "IPFS Metadata", url: normalized });
      }
      // IPFS image
      if (i.imageGatewayUrl) {
        links.push({ label: "IPFS Image", url: i.imageGatewayUrl });
      } else if (i.imageIpfsUri) {
        const normalized = ipfsToHttp(i.imageIpfsUri) || i.imageIpfsUri;
        links.push({ label: "IPFS Image", url: normalized });
      }
      return links;
    });

    // Helper to detect audio URLs
    const isAudioUrl = (url) => {
      if (!url) return false;
      return /(\.(mp3|wav|ogg|m4a|aac|flac|wma))(\?|$)/i.test(url);
    };

    // Helper to detect video URLs
    const isVideoUrl = (url) => {
      if (!url) return false;
      return /(\.(mp4|webm|mov|m4v))(\?|$)/i.test(url);
    };

    // Check if item is primarily a video item
    const isVideoItem = computed(() => {
      if (!props.item) return false;
      // Check mediaType
      if (props.item.mediaType === "video") return true;
      // Check if images array contains a video
      if (Array.isArray(props.item.images)) {
        return props.item.images.some((url) => isVideoUrl(url));
      }
      // Check mediaUrl
      if (props.item.mediaUrl && isVideoUrl(props.item.mediaUrl)) return true;
      return false;
    });

    // Check if item is primarily an audio item
    const isAudioItem = computed(() => {
      if (!props.item) return false;
      // Check mediaType
      if (props.item.mediaType === "audio") return true;
      // Check mediaUrl
      if (props.item.mediaUrl && isAudioUrl(props.item.mediaUrl)) return true;
      // Check audioUrl field
      if (props.item.audioUrl && isAudioUrl(props.item.audioUrl)) return true;
      return false;
    });

    // Check if item is an embed (YouTube/SoundCloud)
    const isEmbedItem = computed(() => {
      if (!props.item) return false;
      const isEmbed = props.item.mediaType === "embed" || !!props.item.embedHtml;
      console.log("[Overlay] isEmbedItem computed:", isEmbed, { mediaType: props.item.mediaType, hasEmbedHtml: !!props.item.embedHtml });
      return isEmbed;
    });

    const isSoundCloudEmbed = computed(() => {
      if (!isEmbedItem.value) return false;
      if (props.item?.embedProvider === "soundcloud") return true;
      return /soundcloud\.com/i.test(props.item?.embedHtml || "");
    });

    const isSpotifyEmbed = computed(() => {
      if (!isEmbedItem.value) return false;
      if (props.item?.embedProvider === "spotify") return true;
      return SPOTIFY_URL_RE.test(props.item?.embedUrl || "") || /open\.spotify\.com/i.test(props.item?.embedHtml || "");
    });

    const embedExternalUrl = computed(() => {
      return normalizeExternalUrl(props.item?.embedUrl || "") || deriveSpotifyOpenUrl(props.item?.embedHtml || "");
    });

    const isVerticalEmbedItem = computed(() => {
      return isEmbedItem.value && isVerticalEmbed(props.item);
    });

    // Sanitized embed HTML for overlay display
    const sanitizedEmbedOverlay = computed(() => {
      if (!props.item?.embedHtml) {
        console.log("[Overlay] sanitizedEmbedOverlay: no embedHtml");
        return "";
      }
      let result = sanitizeEmbedHtml(props.item.embedHtml);
      // Inject enablejsapi for YouTube so we can track play state via postMessage
      result = injectYouTubeApiParams(result);
      console.log("[Overlay] sanitizedEmbedOverlay: sanitized", { inputLen: props.item.embedHtml.length, outputLen: result.length, preview: result.substring(0, 120) });
      return result;
    });

    // Check if item is a poster (text-only, no caption)
    const isPosterItem = computed(() => {
      if (!props.item) return false;
      return props.item.mediaType === "poster";
    });

    // Check if item is a tweet (≤140 chars)
    const isTweetItem = computed(() => {
      if (!props.item) return false;
      return props.item.mediaType === "tweet";
    });

    const toText = (value) => {
      if (value === null || value === undefined) return "";
      if (typeof value === "string") return value;
      if (typeof value === "number" || typeof value === "boolean")
        return String(value);
      if (Array.isArray(value)) {
        return value
          .map((v) => toText(v))
          .map((v) => v.trim())
          .filter(Boolean)
          .join("\n");
      }
      if (typeof value === "object") {
        if (typeof value.text === "string") return value.text;
        if (typeof value.value === "string") return value.value;
        if (typeof value.message === "string") return value.message;
      }
      return "";
    };

    // Check if item is a post (text + media) - either by mediaType or by having any description with images.
    // NOTE: Posts created via PostComposer can have short descriptions; those should still be treated as posts.
    const isPostItem = computed(() => {
      if (!props.item) return false;
      // NFTs have their own dedicated branch — never treat as a post
      if (isNFTType.value) return false;
      // Explicit post mediaType
      if (props.item.mediaType === "post") return true;

      const hasImages =
        Array.isArray(props.item.images) && props.item.images.length > 0;

      const captionText = toText(
        props.item.caption ||
          props.item.description ||
          props.item.itemDescription ||
          "",
      );
      const hasAnyText = captionText.trim().length > 0;

      // If it has images and any description text, treat it as a post.
      // (Keeps tweet/poster behavior controlled by mediaType.)
      // Audio items have their own dedicated branch
      if (props.item.mediaType === "audio") return false;
      if (hasImages && hasAnyText) return true;

      // Mixed media (multiple images, or images + video) — treat as post
      if (hasImages && props.item.images.length > 1) return true;
      if (hasImages && props.item.mediaUrl && !props.item.images.includes(props.item.mediaUrl)) return true;

      return false;
    });

    // Check if item is a document
    const isDocumentItem = computed(() => {
      if (!props.item) return false;
      return props.item.mediaType === "documents";
    });

    // Check if item is a product (excludes NFTs — they have their own branch)
    const isProductItem = computed(() => {
      if (!props.item) return false;
      if (isNFTType.value) return false;
      return props.item.type === "product";
    });

    // Check if item is an NFT (Unit-minted, external listing, or on-chain)
    const isNFTType = computed(() => {
      if (!props.item) return false;
      return props.item.type === "nft" || props.item.isNFTListing || props.item.mediaType === "nft";
    });

    // NFT helper: truncate contract address for display
    const formatAddress = (addr) => {
      if (!addr || addr.length < 10) return addr || "";
      return `${addr.slice(0, 6)}…${addr.slice(-4)}`;
    };

    // NFT helper: check if URL is an IPFS URL
    const isIPFSUrl = (url) => {
      if (!url) return false;
      return url.startsWith("ipfs://") || url.includes("/ipfs/") || url.includes("ipfs.io");
    };

    // Formatted price - shows converted price for non-owners, original for owners
    // NFT listings use listingPrice (crypto) instead of price (Unit coins = 0)
    const formattedPrice = computed(() => {
      if (!props.item) return "";
      // For NFT listings, prefer listingPrice over price
      const basePrice = (props.item.type === "nft" || props.item.isNFTListing)
        ? (props.item.listingPrice || props.item.price || 0)
        : props.item.price;
      if (basePrice === undefined) return "";
      // Use converted price if available (non-owner viewing)
      const priceToDisplay =
        convertedPrice.value !== null ? convertedPrice.value : basePrice;
      return priceToDisplay.toLocaleString();
    });

    // Check if item is a service
    const isServiceItem = computed(() => {
      if (!props.item) return false;
      return props.item.type === "service";
    });

    // Display title (used across all types)
    // For posts: skip title when it duplicates the description body (legacy data issue)
    const displayTitle = computed(() => {
      if (!props.item) return "";
      const raw = (props.item.title || props.item.itemName || "").trim();
      if (!raw) return "";
      // If the description contains the same text, the title is redundant
      const desc = (props.item.description || props.item.caption || "").trim();
      if (desc && raw.length > 140) {
        // Strip whitespace/newlines for comparison — legacy data stores description as title without breaks
        const normTitle = raw.replace(/\s+/g, " ");
        const normDesc = desc.replace(/\s+/g, " ");
        if (normDesc.startsWith(normTitle) || normTitle.startsWith(normDesc)) return "";
      }
      return smartTitleCase(raw);
    });

    // Check if item is title-only (no media, no description - just a title)
    const isTitleOnlyPost = computed(() => {
      if (!props.item) return false;
      const hasTitle = !!(props.item.title || props.item.itemName);
      const hasImages =
        Array.isArray(props.item.images) && props.item.images.length > 0;
      const hasDescription = !!(
        props.item.caption ||
        props.item.description ||
        props.item.itemDescription
      )
        ?.toString()
        .trim();
      // Title-only: has title, no images, no description
      return hasTitle && !hasImages && !hasDescription;
    });

    // Display description — always the post body (description preferred over caption)
    // Per-image captions are handled separately by currentImageCaption
    const displayDescription = computed(() => {
      if (!props.item) return "";
      return toText(
        props.item.description ||
          props.item.caption ||
          props.item.itemDescription ||
          "",
      );
    });

    // Per-image caption from imageCaptions array (if available for current index)
    const currentImageCaption = computed(() => {
      if (!props.item) return "";
      const imageCaptions = Array.isArray(props.item.imageCaptions)
        ? props.item.imageCaptions
        : [];
      if (!imageCaptions.length) return "";
      const idx = currentIndex.value;
      const cap = idx >= 0 && idx < imageCaptions.length
        ? (imageCaptions[idx] || "").toString().trim()
        : "";
      return cap;
    });

    // Post-level description (whole post body). This is intentionally NOT per-image captions.
    const postBodyDescription = computed(() => {
      if (!props.item) return "";
      return toText(
        props.item.description ||
          props.item.caption ||
          props.item.itemDescription ||
          "",
      );
    });

    // Tweet background image (first image if available)
    const tweetBackgroundImage = computed(() => {
      if (!props.item) return "";
      if (Array.isArray(props.item.images) && props.item.images.length > 0) {
        return props.item.images[0];
      }
      return "";
    });

    // Post media URL (first media from images or mediaUrl)
    const postMediaUrl = computed(() => {
      if (!props.item) return "";
      if (props.item.mediaUrl) return props.item.mediaUrl;
      if (Array.isArray(props.item.images) && props.item.images.length > 0) {
        return props.item.images[0];
      }
      return "";
    });

    // Check if post media is video
    const isPostMediaVideo = computed(() => {
      return isVideoUrl(postMediaUrl.value);
    });

    // Check if post media is audio
    const isPostMediaAudio = computed(() => {
      return isAudioUrl(postMediaUrl.value);
    });

    // Get all post media items (images, videos, documents)
    const postMediaItems = computed(() => {
      if (!props.item) return [];

      const imgs = Array.isArray(props.item.images) ? props.item.images : [];
      const imageCaptions = Array.isArray(props.item.imageCaptions)
        ? props.item.imageCaptions
        : [];
      const mediaThumbnails = Array.isArray(props.item.mediaThumbnails)
        ? props.item.mediaThumbnails
        : [];

      // ── Standalone video post ──────────────────────────────────
      // For mediaType === 'video', images[] contains [poster, videoUrl] —
      // collapse into a single video item with a poster attribute instead
      // of leaking the poster as a standalone image slide.
      // Only applies to truly standalone videos (≤1 non-video image = just a poster).
      // Complex/mixed posts (video + multiple images) fall through to the general case.
      const nonVideoImages = imgs.filter((u) => u && !isVideoUrl(u));
      if (props.item.mediaType === "video" && nonVideoImages.length <= 1) {
        const tIdx = props.item.thumbnailIndex ?? 0;
        const posterCandidate = imgs[tIdx] ?? imgs[0] ?? "";
        const posterImg = posterCandidate && !isVideoUrl(posterCandidate)
          ? posterCandidate
          : null;
        const videoSrc =
          props.item.mediaUrl ||
          imgs.find((u) => isVideoUrl(u)) ||
          imgs.find((u, i) => i !== tIdx && u) ||
          "";
        const caption = toText(imageCaptions[0] || "").trim();
        return videoSrc
          ? [{ type: "video", url: videoSrc, index: 0, caption, poster: posterImg }]
          : [];
      }

      // ── General case (photos, post, embed, etc.) ───────────────
      const items = [];

      // Build a set of poster URLs from mediaThumbnails to exclude from image slides
      const posterUrlSet = new Set(mediaThumbnails.filter(Boolean));

      // Collect from images[]
      if (imgs.length) {
        imgs.forEach((url, idx) => {
          const caption = toText(imageCaptions[idx] || "").trim();
          if (isVideoUrl(url)) {
            // Attach per-video poster from mediaThumbnails[] if available
            const poster = mediaThumbnails[idx] || null;
            items.push({ type: "video", url, index: idx, caption, poster });
          } else if (isAudioUrl(url)) {
            items.push({ type: "audio", url, index: idx, caption });
          } else if (url.match(/\.(pdf|doc|docx|xls|xlsx|ppt|pptx)$/i)) {
            items.push({ type: "document", url, index: idx, caption });
          } else if (posterUrlSet.has(url)) {
            // Skip — this is a video poster stored in images[] by legacy code
          } else {
            items.push({ type: "image", url, index: idx, caption });
          }
        });
      }

      // Add mediaUrl if not already in images
      if (
        props.item.mediaUrl &&
        !items.find((i) => i.url === props.item.mediaUrl)
      ) {
        const url = props.item.mediaUrl;
        if (isVideoUrl(url)) {
          // Try to find a poster from mediaThumbnails (any non-empty entry)
          const videoPoster = mediaThumbnails.find((p) => p) || null;
          items.unshift({ type: "video", url, index: -1, caption: "", poster: videoPoster });
        } else if (isAudioUrl(url)) {
          items.unshift({ type: "audio", url, index: -1, caption: "" });
        } else {
          items.unshift({ type: "image", url, index: -1, caption: "" });
        }
      }

      // Add document URLs
      if (Array.isArray(props.item.documentUrls)) {
        props.item.documentUrls.forEach((url, idx) => {
          if (!items.find((i) => i.url === url)) {
            items.push({ type: "document", url, index: idx, caption: "" });
          }
        });
      }

      // ── Move designated thumbnail to first position ────────────
      // The thumbnailIndex item should be the hero/first in the overlay
      if (items.length > 1) {
        const tIdx = props.item.thumbnailIndex ?? 0;
        const thumbPos = items.findIndex((i) => i.index === tIdx);
        if (thumbPos > 0) {
          const [thumbItem] = items.splice(thumbPos, 1);
          items.unshift(thumbItem);
        }
      }

      return items;
    });

    // Media items formatted for OrganicMediaGrid component
    const postMediaItemsForGrid = computed(() => {
      return postMediaItems.value.map((item) => ({
        url: item.url,
        type: item.type,
        caption: item.caption,
        poster: item.poster,
        cover: item.cover,
      }));
    });

    // Truncate caption at 4× the ImageCard default (120 × 4 = 480)
    const truncateCaption = (text) => truncateText(text, 480);

    // Media zoom overlay state
    const showMediaZoom = ref(false);
    const zoomMediaIndex = ref(0);
    const galleryVideoRefs = ref([]);

    // Handle click on media item in organic grid
    const onOrganicMediaClick = ({ item, index }) => {
      if (!item) return;
      const type = item.type;
      if (type === 'video') {
        ultraMediaType.value = 'video';
      } else if (type === 'document' && /\.pdf$/i.test(item.url || '')) {
        ultraMediaType.value = 'pdf';
      } else {
        ultraMediaType.value = 'image';
      }
      ultraMediaUrl.value = item.url || '';
      ultraMediaPoster.value = item.poster || '';
      showUltraOverlay.value = true;
    };

    const openMediaFullscreen = (mediaItem) => {
      onOrganicMediaClick({ item: mediaItem, index: 0 });
    };

    const pauseGalleryVideos = () => {
      const videoElements = Array.isArray(galleryVideoRefs.value)
        ? galleryVideoRefs.value
        : [galleryVideoRefs.value];
      videoElements.filter(Boolean).forEach((videoElement) => {
        if (!videoElement.paused) videoElement.pause();
      });
    };

    // Ultra overlay state (fullscreen single-media viewer)
    const showUltraOverlay = ref(false);
    const ultraMediaUrl = ref('');
    const ultraMediaPoster = ref('');
    const ultraMediaType = ref('image');

    const closeUltraOverlay = () => {
      showUltraOverlay.value = false;
    };

    // Close media zoom overlay
    const closeMediaZoom = () => {
      showMediaZoom.value = false;
    };

    // The resolver decides presentation independently from the persisted media type.
    // Dedicated formats retain their existing branches; NFT remains decorated by its
    // existing metadata/actions branch until the reusable NFT viewer work lands.
    const presentationMode = computed(() =>
      resolvePresentationMode(props.item),
    );

    const isArticlePresentation = computed(
      () =>
        presentationMode.value === PRESENTATION_MODES.ARTICLE &&
        !isNFTType.value,
    );
    const isStoryPresentation = computed(
      () =>
        presentationMode.value === PRESENTATION_MODES.STORY &&
        !isNFTType.value,
    );
    const isGalleryPresentation = computed(
      () =>
        presentationMode.value === PRESENTATION_MODES.GALLERY &&
        !isNFTType.value,
    );
    const isTextStoryPresentation = computed(
      () => presentationMode.value === PRESENTATION_MODES.TEXT_STORY,
    );

    // Preserve the richer overlay media records (captions, posters, cover order)
    // and fall back to the pure resolver's legacy normalization when needed.
    const presentationVisualMediaItems = computed(() => {
      const visualItems = postMediaItems.value.filter(
        (mediaItem) =>
          mediaItem.type === "image" || mediaItem.type === "video",
      );
      if (visualItems.length > 0) return visualItems;
      return getVisualMedia(props.item);
    });

    const storyMediaItem = computed(
      () => presentationVisualMediaItems.value[0] || null,
    );
    const galleryMediaItems = computed(
      () => presentationVisualMediaItems.value,
    );
    const currentGalleryItem = computed(() => {
      return (
        galleryMediaItems.value[currentIndex.value] ||
        galleryMediaItems.value[0] ||
        null
      );
    });
    const fullscreenMediaItem = computed(() => {
      if (isStoryPresentation.value) return storyMediaItem.value;
      if (isGalleryPresentation.value) return currentGalleryItem.value;
      return null;
    });
    const onGalleryMobileScroll = (event) => {
      const container = event?.currentTarget;
      const galleryItems = container?.querySelectorAll?.(
        "[data-gallery-index]",
      );
      if (!container || !galleryItems?.length) return;

      const containerRect = container.getBoundingClientRect();
      let visibleIndex = currentIndex.value;
      let greatestVisibleHeight = -1;

      galleryItems.forEach((galleryItem) => {
        const itemRect = galleryItem.getBoundingClientRect();
        const visibleHeight = Math.max(
          0,
          Math.min(itemRect.bottom, containerRect.bottom) -
            Math.max(itemRect.top, containerRect.top),
        );
        const itemIndex = Number(galleryItem.dataset.galleryIndex);
        if (
          Number.isInteger(itemIndex) &&
          visibleHeight > greatestVisibleHeight
        ) {
          visibleIndex = itemIndex;
          greatestVisibleHeight = visibleHeight;
        }
      });

      currentIndex.value = visibleIndex;
    };
    const storyUsesMobileCover = computed(() => {
      if (!storyMediaItem.value?.url) return false;
      const ratio = mediaAspectByUrl.value[storyMediaItem.value.url];
      return shouldCoverStoryMedia(ratio, isMobileViewport.value);
    });

    // Compatibility flags used by the remaining overlay behavior.
    const isMultiMediaPost = computed(() => {
      if (!isPostItem.value) return false;
      if (isDocumentItem.value) return false;
      if (isAudioItem.value) return false;
      return postMediaItems.value.length > 1;
    });

    const isMultiMediaSlideshowPost = computed(() => {
      return isGalleryPresentation.value;
    });

    const isSingleMediaPost = computed(() => {
      return isStoryPresentation.value;
    });

    const isComplexPost = computed(() => {
      return isArticlePresentation.value;
    });

    const isSimplePostItem = computed(() => {
      return isPostItem.value && isStoryPresentation.value;
    });

    // Parse description into paragraphs for blog-style layout
    const descriptionParagraphs = computed(() => {
      const text = postBodyDescription.value || "";
      if (!text.trim()) return [];

      // Split by double newlines (paragraph breaks) or single newlines
      const paragraphs = text
        .split(/\n\n+|\n/)
        .map((p) => p.trim())
        .filter((p) => p.length > 0);

      return paragraphs;
    });

    // Map description paragraphs to content blocks, detecting standalone YouTube URLs
    const YT_URL_RE = /^(https?:\/\/)([\w-]+\.)*youtube\.com\/\S+$|^(https?:\/\/)?(www\.)?youtu\.be\/\S+$/i;
    const blogContentBlocks = computed(() => {
      return descriptionParagraphs.value.map((paragraph, idx) => {
        const trimmed = paragraph.trim();
        // If the entire paragraph is a single YouTube URL, render as embed
        if (YT_URL_RE.test(trimmed)) {
          const videoId = extractYouTubeId(trimmed);
          if (videoId) {
            const html = `<iframe src="https://www.youtube-nocookie.com/embed/${videoId}" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" referrerpolicy="strict-origin-when-cross-origin" allowfullscreen loading="lazy"></iframe>`;
            return { type: 'youtube', videoId, html: sanitizeEmbedHtml(html), key: `yt-${idx}` };
          }
        }
        return { type: 'text', text: paragraph, key: `text-${idx}` };
      });
    });

    // Check if item is text-only (no media to load)
    // Poster, tweet without image, document, embed, and post without media bypass loading
    const isTextOnlyItem = computed(() => {
      if (isPosterItem.value) return true;
      if (isEmbedItem.value) return true; // Embed iframes handle their own loading
      if (isTweetItem.value && !tweetBackgroundImage.value) return true;
      if (isPostItem.value && !postMediaUrl.value) return true;
      if (isDocumentItem.value) return true;
      return false;
    });

    // Document URL (first document from documentUrls or images)
    const documentUrl = computed(() => {
      if (!props.item) return "";
      if (
        Array.isArray(props.item.documentUrls) &&
        props.item.documentUrls.length > 0
      ) {
        return props.item.documentUrls[0];
      }
      if (Array.isArray(props.item.images) && props.item.images.length > 0) {
        return props.item.images[0];
      }
      return "";
    });

    // --- Document description overflow detection ---
    const docDescriptionRef = ref(null);
    const docDescOverflows = ref(false);
    const checkDocDescOverflow = () => {
      nextTick(() => {
        const el = docDescriptionRef.value;
        if (el) {
          docDescOverflows.value = el.scrollHeight > el.clientHeight;
        } else {
          docDescOverflows.value = false;
        }
      });
    };
    watch(displayDescription, checkDocDescOverflow);

    // Document filename
    const documentFilename = computed(() => {

      if (!props.item) return "Document";
      // Check documentMetadata first
      if (
        Array.isArray(props.item.documentMetadata) &&
        props.item.documentMetadata.length > 0
      ) {
        const meta = props.item.documentMetadata[0];
        if (meta?.name) return meta.name;
      }
      // Extract from URL
      const url = documentUrl.value;
      if (url) {
        try {
          const pathname = new URL(url).pathname;
          const decoded = decodeURIComponent(pathname.split("/").pop() || "");
          // Remove timestamp prefix if present (e.g., "1234567890_filename.pdf")
          const cleaned = decoded.replace(/^\d+_/, "");
          if (cleaned) return cleaned;
        } catch {
          // Fallback
        }
      }
      return props.item.title || props.item.itemName || "Document";
    });

    // Is the primary document a PDF?
    const isPdfDocument = computed(() => {
      if (!isDocumentItem.value) return false;
      // Check metadata type first
      if (
        Array.isArray(props.item?.documentMetadata) &&
        props.item.documentMetadata.length > 0
      ) {
        const firstDoc = props.item.documentMetadata[0];
        if (firstDoc?.type === "application/pdf") return true;
      }
      // Fallback to URL extension
      const url = documentUrl.value;
      if (!url) return false;
      return /\.pdf(\?|$)/i.test(url);
    });

    // Is the primary document a text-readable file?
    const isTextDocument = computed(() => {
      if (!isDocumentItem.value) return false;
      const meta = props.item?.documentMetadata;
      if (Array.isArray(meta) && meta.length) {
        const firstDoc = meta[0];
        const type = (firstDoc?.type || "").toLowerCase();
        const name = (firstDoc?.name || "").toLowerCase();
        if (
          type === "text/plain" || type === "text/markdown" ||
          type === "text/csv" || type === "application/json" ||
          name.endsWith(".txt") || name.endsWith(".md") ||
          name.endsWith(".csv") || name.endsWith(".json")
        ) return true;
      }
      const url = documentUrl.value;
      if (url && /\.(txt|md|csv|json)(\?|$)/i.test(url)) return true;
      if (props.item?.documentTextContents && Object.keys(props.item.documentTextContents).length) return true;
      return false;
    });

    // Extract file extension for the badge
    const documentExtension = computed(() => {
      if (!isDocumentItem.value) return "";
      const meta = props.item?.documentMetadata;
      if (Array.isArray(meta) && meta.length && meta[0]?.name) {
        const name = meta[0].name;
        const ext = name.split(".").pop();
        if (ext && ext !== name) return ext.toUpperCase();
      }
      const url = documentUrl.value;
      if (url) {
        try {
          const pathname = new URL(url).pathname;
          const ext = pathname.split(".").pop();
          if (ext && ext.length <= 6) return ext.toUpperCase();
        } catch { /* ignore */ }
      }
      return "";
    });

    // Text content for text document preview
    const documentTextContent = ref("");
    const fetchDocTextContent = async () => {
      if (!isTextDocument.value) { documentTextContent.value = ""; return; }
      // Check inline text contents first
      if (props.item?.documentTextContents) {
        const keys = Object.keys(props.item.documentTextContents);
        if (keys.length) {
          documentTextContent.value = props.item.documentTextContents[keys[0]];
          return;
        }
      }
      // Try fetching the URL
      const url = documentUrl.value;
      if (!url) { documentTextContent.value = ""; return; }
      try {
        const resp = await fetch(url);
        if (resp.ok) {
          const text = await resp.text();
          // Limit to first 5000 chars for preview
          documentTextContent.value = text.length > 5000 ? text.substring(0, 5000) + "\n…" : text;
        }
      } catch {
        documentTextContent.value = "";
      }
    };

    // Fetch text content when item changes
    watch(
      () => props.item?.id,
      () => { if (isDocumentItem.value && isTextDocument.value) fetchDocTextContent(); },
      { immediate: true }
    );

    // Get audio URL for audio items
    const audioUrl = computed(() => {
      if (!props.item) return "";
      return props.item.mediaUrl || props.item.audioUrl || "";
    });

    // Get cover image for audio items
    const audioCoverImage = computed(() => {
      if (!props.item) return "";

      // Check images array
      if (Array.isArray(props.item.images) && props.item.images.length > 0) {
        // Find first non-audio image
        const img = props.item.images.find((url) => url && !isAudioUrl(url));
        if (img) return img;
      }

      // Check thumbnail field
      if (props.item.thumbnail) return props.item.thumbnail;

      // Check variants
      const v = props.item.variants || props.item.imageVariants;
      if (v && typeof v === "object") {
        return v.md || v.lg || v.sm || v.xl || Object.values(v)[0] || "";
      }

      return "";
    });

    // Audio element ref
    const audioRef = ref(null);

    // Feed cards carry at most four signed item image URLs, which expire
    // after a few minutes; the viewer signs the item's full set when opened.
    const authorityItemImages = ref(null);
    let authorityItemImagesGeneration = 0;
    watch(
      () =>
        props.item?._authorityPresentation === true &&
        Array.isArray(props.item?.mediaAssetIds) &&
        props.item.mediaAssetIds.length > 0
          ? props.item
          : null,
      async (item) => {
        const generation = ++authorityItemImagesGeneration;
        authorityItemImages.value = null;
        if (!item) return;
        const urls = await itemMediaUrlResolver.resolveImageUrls(
          item.mediaAssetIds,
        );
        if (generation === authorityItemImagesGeneration && urls.length > 0) {
          authorityItemImages.value = urls;
        }
      },
      { immediate: true },
    );

    // Extract media array from item
    // For video items: use mediaUrl or find video in images array
    // For image items: include all images
    const media = computed(() => {
      if (!props.item) return [];
      if (authorityItemImages.value) return authorityItemImages.value;

      // For video items, prioritize mediaUrl
      if (isVideoItem.value) {
        // First check mediaUrl (most common for videos)
        if (props.item.mediaUrl) {
          return [props.item.mediaUrl];
        }
        // Then check for video URLs in images array
        if (Array.isArray(props.item.images)) {
          const videos = props.item.images.filter((url) => isVideoUrl(url));
          if (videos.length > 0) return videos;
        }
        // Check alternative video fields
        const altVideo =
          props.item.videoUrl ||
          props.item.processedVideoUrl ||
          props.item.originalVideoUrl;
        if (altVideo) return [altVideo];
      }

      // For non-video items, return images (excluding any video files)
      if (Array.isArray(props.item.images) && props.item.images.length > 0) {
        return props.item.images.filter((url) => !isVideoUrl(url));
      }

      // Fallback to mediaUrl (for non-video)
      if (props.item.mediaUrl) {
        return [props.item.mediaUrl];
      }

      // Fallback to image
      if (props.item.image) {
        return [props.item.image];
      }

      return [];
    });

    // Keep 'images' as alias for backward compatibility
    const images = media;

    const hasMultipleImages = computed(() => media.value.length > 1);

    const totalMultiSlides = computed(() => {
      if (isGalleryPresentation.value) return galleryMediaItems.value.length;
      if (!isMultiMediaPost.value) return media.value.length;
      return postMediaItems.value.length + 1; // +1 for text slide
    });

    // Current media item for multi-media slideshow
    const currentMultiMediaItem = computed(() => {
      if (!isMultiMediaPost.value) return null;
      if (currentIndex.value >= postMediaItems.value.length) return null; // On text slide
      return postMediaItems.value[currentIndex.value];
    });

    // Check if currently on the text slide (last slide)
    const isOnTextSlide = computed(() => {
      if (!isMultiMediaPost.value) return false;
      return currentIndex.value >= postMediaItems.value.length;
    });

    // Show navigation arrows for multi-image items AND multi-media posts
    const showNavigationArrows = computed(() => {
      if (isArticlePresentation.value) return false;
      if (isGalleryPresentation.value) {
        return !isMobileViewport.value && galleryMediaItems.value.length > 1;
      }
      if (isMultiMediaPost.value) return true;
      return hasMultipleImages.value;
    });
    const showNavigationCounter = computed(() => {
      if (isArticlePresentation.value) return false;
      if (isGalleryPresentation.value) {
        return !isMobileViewport.value && galleryMediaItems.value.length > 1;
      }
      if (isMultiMediaPost.value) return true;
      return hasMultipleImages.value;
    });

    const currentMediaUrl = computed(() => {
      if (media.value.length === 0) return "";
      return media.value[currentIndex.value] || media.value[0];
    });

    const isCurrentMediaVideo = computed(() => {
      return isVideoUrl(currentMediaUrl.value);
    });

    const imageAlt = computed(() => {
      return props.item?.title || props.item?.itemName || "Media";
    });

    // Video element ref
    const videoRef = ref(null);
    const isVideoPaused = ref(true);

    // Get poster/thumbnail for video (matching ImageCard logic)
    const videoPoster = computed(() => {
      if (!props.item) return "";

      const imgs = Array.isArray(props.item.images) ? props.item.images : [];

      // For video posts, use thumbnailIndex to get the poster
      if (props.item.mediaType === "video" && imgs.length) {
        const tIdx = props.item.thumbnailIndex ?? 0;
        const candidate = imgs[tIdx] ?? imgs[0];
        // Avoid returning the mediaUrl if it's in images
        if (candidate && candidate !== props.item.mediaUrl) return candidate;
      }

      // Check for explicit thumbnail field
      if (props.item.thumbnail) return props.item.thumbnail;

      // Find first non-video image in the array
      const imageLike = imgs.find((url) => url && !isVideoUrl(url));
      if (imageLike) return imageLike;

      // Check variants
      const v = props.item.variants || props.item.imageVariants;
      if (v && typeof v === "object") {
        return v.md || v.lg || v.sm || v.xl || Object.values(v)[0] || "";
      }

      return "";
    });

    // Thumbnail for minimized bar: prefer poster/thumbnail, fallback to current media
    const minimizedThumbnail = computed(() => {
      if (videoPoster.value) return videoPoster.value;
      if (currentMediaUrl.value && !isCurrentMediaVideo.value)
        return currentMediaUrl.value;
      // For posts, check first image
      const imgs = props.item?.images;
      if (Array.isArray(imgs) && imgs.length) {
        const img = imgs.find((u) => u && !isVideoUrl(u));
        if (img) return img;
      }
      return "";
    });

    // Pause video before navigating away
    const pauseCurrentVideo = () => {
      if (videoRef.value) {
        videoRef.value.pause();
      }
    };

    // Play video (called from play overlay)
    const playVideo = () => {
      if (videoRef.value) {
        videoRef.value.play().catch(() => {
          // Autoplay blocked, user must interact
          console.log("Playback requires user interaction");
        });
      }
    };

    // Video event handlers
    const onVideoLoaded = (event) => {
      onMediaLoad(event);
      // Try to autoplay unmuted
      if (videoRef.value) {
        videoRef.value.play().catch(() => {
          // Autoplay was blocked, show play button
          isVideoPaused.value = true;
        });
      }
    };

    const onVideoPlay = () => {
      isVideoPaused.value = false;
    };

    const onVideoPause = () => {
      isVideoPaused.value = true;
    };

    // Slide direction for animation ('slide-left' or 'slide-right')
    const slideDirection = ref("slide-left");

    const prevImage = () => {
      if (currentIndex.value > 0) {
        slideDirection.value = "slide-right";
        pauseCurrentVideo();
        pauseGalleryVideos();
        isLoading.value = true;
        isVideoPaused.value = true;
        currentIndex.value--;
      }
    };

    const nextImage = () => {
      const maxIndex = isGalleryPresentation.value
        ? galleryMediaItems.value.length - 1
        : isMultiMediaPost.value
          ? totalMultiSlides.value - 1
          : media.value.length - 1;
      if (currentIndex.value < maxIndex) {
        slideDirection.value = "slide-left";
        pauseCurrentVideo();
        pauseGalleryVideos();
        isLoading.value = true;
        isVideoPaused.value = true;
        currentIndex.value++;
      }
    };

    // Jump to the text slide (last slide) for multi-media posts
    const goToTextSlide = () => {
      if (!isMultiMediaPost.value) return;
      slideDirection.value = "slide-left";
      pauseCurrentVideo();
      isLoading.value = false; // Text doesn't need loading
      currentIndex.value = postMediaItems.value.length; // Index of text slide
    };

    // Go back to first slide from text slide
    const goToFirstSlide = () => {
      if (!isMultiMediaPost.value) return;
      slideDirection.value = "slide-right";
      isLoading.value = true;
      currentIndex.value = 0;
    };

    // Horizontal motion belongs to media slides; vertical motion belongs to
    // feed items. The direction also drives the keyed content transition.
    const itemNavigationDirection = ref(null);
    const contentTransitionName = computed(() => {
      if (itemNavigationDirection.value === "next") return "post-up";
      if (itemNavigationDirection.value === "prev") return "post-down";
      return "slideshow-shrink";
    });
    let itemNavigationDirectionTimer = null;
    const clearItemNavigationDirectionLater = () => {
      if (itemNavigationDirectionTimer) {
        clearTimeout(itemNavigationDirectionTimer);
      }
      itemNavigationDirectionTimer = setTimeout(() => {
        itemNavigationDirection.value = null;
        itemNavigationDirectionTimer = null;
      }, 350);
    };
    const navigateToNextPost = () => {
      if (!props.canNavigateNext) return;
      itemNavigationDirection.value = "next";
      emit("next");
    };
    const navigateToPrevPost = () => {
      if (!props.canNavigatePrevious) return;
      itemNavigationDirection.value = "prev";
      emit("prev");
    };

    // Touch swipe support for mobile
    const touchStartX = ref(0);
    const touchStartY = ref(0);
    const touchEndX = ref(0);
    const touchEndY = ref(0);
    const touchCanNavigateNext = ref(false);
    const touchCanNavigatePrevious = ref(false);

    const onTouchStart = (e) => {
      if (!e.touches || e.touches.length === 0) return;
      touchStartX.value = e.touches[0].clientX;
      touchStartY.value = e.touches[0].clientY;
      touchEndX.value = e.touches[0].clientX;
      touchEndY.value = e.touches[0].clientY;
      touchCanNavigateNext.value = canLeaveScrollableContent({
        target: e.target,
        boundary: overlayRef.value,
        direction: "next",
      });
      touchCanNavigatePrevious.value = canLeaveScrollableContent({
        target: e.target,
        boundary: overlayRef.value,
        direction: "prev",
      });
    };

    const onTouchMove = (e) => {
      if (!e.touches || e.touches.length === 0) return;
      touchEndX.value = e.touches[0].clientX;
      touchEndY.value = e.touches[0].clientY;
    };

    const onTouchEnd = () => {
      const gesture = classifyOverlaySwipe({
        startX: touchStartX.value,
        startY: touchStartY.value,
        endX: touchEndX.value,
        endY: touchEndY.value,
      });

      if (gesture === "slide-next") nextImage();
      if (gesture === "slide-prev") prevImage();
      if (gesture === "item-next" && touchCanNavigateNext.value) {
        navigateToNextPost();
      }
      if (gesture === "item-prev" && touchCanNavigatePrevious.value) {
        navigateToPrevPost();
      }
    };

    let wheelDistance = 0;
    let wheelResetTimer = null;
    let wheelCooldownUntil = 0;
    const onWheel = (event) => {
      if (Math.abs(event.deltaY) <= Math.abs(event.deltaX)) return;
      const direction = event.deltaY > 0 ? "next" : "prev";
      if (!canLeaveScrollableContent({
        target: event.target,
        boundary: overlayRef.value,
        direction,
      })) return;

      if (event.cancelable) event.preventDefault();

      if (Date.now() < wheelCooldownUntil) return;
      wheelDistance += event.deltaY;
      if (wheelResetTimer) clearTimeout(wheelResetTimer);
      wheelResetTimer = setTimeout(() => { wheelDistance = 0; }, 180);
      if (Math.abs(wheelDistance) < 80) return;

      wheelDistance = 0;
      wheelCooldownUntil = Date.now() + 450;
      direction === "next" ? navigateToNextPost() : navigateToPrevPost();
    };

    const mediaAspectByUrl = ref({});
    const onMediaLoad = (event) => {
      try {
        const target = event?.target;
        const declaredUrl = target?.getAttribute?.("src") || "";
        const url =
          target?.currentSrc ||
          target?.src ||
          declaredUrl ||
          "";

        if (url) {
          let width = 0;
          let height = 0;

          // Images
          if (typeof target?.naturalWidth === "number") {
            width = target.naturalWidth;
            height = target.naturalHeight;
          }

          // Videos (loadeddata)
          if (!width && typeof target?.videoWidth === "number") {
            width = target.videoWidth;
            height = target.videoHeight;
          }

          if (width > 0 && height > 0) {
            const ratio = width / height;
            mediaAspectByUrl.value = {
              ...mediaAspectByUrl.value,
              [url]: ratio,
              ...(declaredUrl ? { [declaredUrl]: ratio } : {}),
            };
          }
        }
      } catch {}

      isLoading.value = false;
    };

    // Check if item is in wishlist when opened
    const checkWishlistStatus = async () => {
      if (!isWishlistFeatureEnabled || isAuthorityPresentation.value) {
        isWishlisted.value = false;
        return;
      }
      if (!currentUserUid.value || !props.item?.id) {
        isWishlisted.value = false;
        return;
      }
      try {
        const wishlistDocRef = doc(
          null,
          "users",
          currentUserUid.value,
          "Wishlist",
          props.item.id,
        );
        const docSnap = await getDoc(wishlistDocRef);
        isWishlisted.value = docSnap.exists();
      } catch (error) {
        console.error("Error checking wishlist status:", error);
        isWishlisted.value = false;
      }
    };

    // Toggle wishlist (add/remove)
    const toggleWishlist = async () => {
      if (!isWishlistFeatureEnabled || isAuthorityPresentation.value) return;
      console.log("toggleWishlist called", {
        currentUserUid: currentUserUid.value,
        itemId: props.item?.id,
        isWishlisted: isWishlisted.value,
      });
      if (!currentUserUid.value) {
        toast.error("You must be logged in to add items to favourites.", {
          autoClose: 3000,
        });
        return;
      }
      if (!props.item?.id) {
        console.error("toggleWishlist: No item ID");
        return;
      }

      try {
        const wishlistDocRef = doc(
          null,
          "users",
          currentUserUid.value,
          "Wishlist",
          props.item.id,
        );
        console.log("Wishlist doc path:", wishlistDocRef.path);

        if (isWishlisted.value) {
          // Remove from wishlist
          console.log("Removing from wishlist...");
          await deleteDoc(wishlistDocRef);
          isWishlisted.value = false;
          console.log("Removed from wishlist successfully");
          toast.success("Item removed from favourites.", { autoClose: 3000 });
        } else {
          // Add to wishlist
          console.log("Adding to wishlist...", props.item);
          await setDoc(wishlistDocRef, {
            ...props.item,
            createdAt: serverTimestamp(),
          });
          isWishlisted.value = true;
          console.log("Added to wishlist successfully");
          toast.success("Item added to favourites.", { autoClose: 3000 });
        }
        // Notify parent to refresh wishlist state
        emit("favouritesUpdated");
      } catch (error) {
        console.error("Error toggling wishlist:", error);
        toast.error("Error updating favourites.", { autoClose: 3000 });
      }
    };

    // Share item - copy product URL to clipboard
    const handleShare = async () => {
      const itemId = props.item?.itemId || props.item?.id;
      if (!itemId) {
        toast.error("Cannot share: no item ID", { autoClose: 3000 });
        return;
      }

      const target = new URL(window.location.href);
      target.searchParams.set("item", itemId);
      if (isAuthorityPresentation.value && props.item?.organisation) {
        target.searchParams.set("itemOrg", props.item.organisation);
      } else {
        target.searchParams.delete("itemOrg");
      }
      const url = target.href;
      console.log("handleShare called", { url, itemId });

      // Try modern clipboard API first (requires HTTPS)
      if (navigator.clipboard && window.isSecureContext) {
        try {
          await navigator.clipboard.writeText(url);
          toast.success("Link copied to clipboard", { autoClose: 3000 });
          return;
        } catch (error) {
          console.error("Clipboard API failed:", error);
        }
      }

      // Fallback for HTTP or older browsers
      try {
        const textArea = document.createElement("textarea");
        textArea.value = url;
        textArea.style.position = "fixed";
        textArea.style.left = "-999999px";
        textArea.style.top = "-999999px";
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        const successful = document.execCommand("copy");
        document.body.removeChild(textArea);
        if (successful) {
          toast.success("Link copied to clipboard", { autoClose: 3000 });
        } else {
          toast.error("Failed to copy link", { autoClose: 3000 });
        }
      } catch (error) {
        console.error("Fallback copy failed:", error);
        toast.error("Failed to copy link", { autoClose: 3000 });
      }
    };

    // Share to Messages — attach item card and open messages panel
    const handleShareToMessages = async () => {
      if (isAuthorityPresentation.value) return;
      const item = props.item;
      if (!item) return;
      // Document items store doc file URLs (PDFs etc.) in images[] — skip those
      const isDoc = item.mediaType === "documents";
      // For YouTube embeds without a stored thumbnail, derive from embedUrl/embedHtml
      let derivedThumbnail = isDoc
        ? ""
        : item.images?.[item.thumbnailIndex || 0] ||
          item.thumbnailUrl ||
          item.thumbnail ||
          item.posterUrl ||
          "";
      if (!derivedThumbnail && item.mediaType === "embed") {
        const src = item.embedUrl || item.embedHtml || "";
        const ytMatch = src.match(/(?:youtube\.com\/(?:embed\/|watch\?v=)|youtu\.be\/)([\w-]{11})/);
        if (ytMatch) derivedThumbnail = `https://img.youtube.com/vi/${ytMatch[1]}/hqdefault.jpg`;
      }
      // Extract document name from metadata (same logic as ImageCard.documentDisplayName)
      let docName = item.documentName || item.fileName || "";
      if (!docName && isDoc && Array.isArray(item.documentMetadata) && item.documentMetadata[0]?.name) {
        docName = item.documentMetadata[0].name;
      }
      const pendingShare = {
        id: item.id || "",
        itemName: item.itemName || item.title || "",
        description: item.description || item.caption || "",
        mediaType: item.mediaType || "",
        type: item.type || "",
        thumbnail: derivedThumbnail,
        embedProvider: item.embedProvider || null,
        owner: item.owner || "",
        originalPath: item.originalPath || null,
        // Commerce fields
        price: item.price || null,
        coinSymbol: item.coinSymbol || "",
        // Document fields
        documentName: docName,
      };
      await store.dispatch("UIState/setPendingShareItem", pendingShare);
      await store.dispatch("UIState/setMessagesMinimised", false);
      await store.dispatch("UIState/setMessagesOpen", true);
    };

    // Handle message - open messages panel to chat with owner
    const handleMessage = async () => {
      if (!props.item?.owner) return;
      try {
        // Fetch the other user data using product object (like ProductView does)
        await store.dispatch("UIState/fetchOtherUserData", {
          product: props.item,
        });
        await store.dispatch("UIState/setMessagesOpen", true);
        await store.dispatch("UIState/setMessagesMaximised", true);
      } catch (error) {
        console.error("Error opening messages:", error);
        toast.error("Failed to open messages", { autoClose: 3000 });
      }
    };

    const handleAuthorityPurchaseSettled = () => {
      if (isAuthorityPresentation.value) emit("purchase-settled");
    };

    // Focus overlay when opened for keyboard support
    watch(
      () => props.visible,
      (newVal) => {
        if (newVal) {
          currentIndex.value = props.startIndex;
          const skipLoading = isEmbedItem.value;
          isLoading.value = !skipLoading && media.value.length > 0;
          console.log("[Overlay] visible changed to true", { isEmbed: isEmbedItem.value, isLoading: isLoading.value });
          isMinimized.value = false;
          selectedSlot.value = { dayIndex: null, hour: null };
          // Reset embed playback tracking for new overlay open
          embedIsPlaying.value = false;
          embedCurrentTime.value = 0;
          checkWishlistStatus();
          fetchAvatarData();
          fetchAvailableSlots();
          checkNFTListingStatus();
          nextTick(() => {
            overlayRef.value?.focus();
            // Setup postMessage listeners for embed iframes
            if (isEmbedItem.value) {
              setTimeout(() => {
                setupYouTubePostMessage();
                setupSoundCloudPostMessage();
              }, 500); // Give iframe time to load
            }
          });
          // Show conflict dialog immediately if floating player is already active on a different item,
          // but skip it if the item is already in the playlist or queue (no need to add again).
          const isPlayableItem = isEmbedItem.value || (props.item?.mediaType === "audio" && !!props.item?.audioUrl);
          if (isPlayableItem) {
            const fpItem = store.getters["UIState/floatingPlayerItem"];
            if (fpItem && fpItem.id !== props.item?.id) {
              const itemId = props.item?.id;
              const fpPlaylist = store.getters["UIState/floatingPlayerPlaylist"] || [];
              const fpQueue = store.getters["UIState/floatingPlayerQueue"] || [];
              const alreadyInList = fpPlaylist.some(i => i.id === itemId) || fpQueue.some(i => i.id === itemId);
              if (!alreadyInList) {
                conflictDialogVisible.value = true;
              }
            }
          }
        } else {
          conflictDialogVisible.value = false;
          stopAvatarDataSubscription();
        }
      },
    );

    // Reset index when item changes
    watch(
      () => props.item,
      () => {
        currentIndex.value = props.startIndex;
        const skipLoading = isEmbedItem.value;
        isLoading.value = !skipLoading && media.value.length > 0;
        console.log("[Overlay] item changed", { isEmbed: isEmbedItem.value, isLoading: isLoading.value, mediaType: props.item?.mediaType });
        isMinimized.value = false;
        selectedSlot.value = { dayIndex: null, hour: null };
        // Reset embed playback tracking when item changes
        embedIsPlaying.value = false;
        embedCurrentTime.value = 0;
        fetchConvertedPrice();
        checkWishlistStatus();
        fetchAvatarData();
        fetchAvailableSlots();
        checkNFTListingStatus();
        clearItemNavigationDirectionLater();
      },
    );
    watch(
      () => props.itemNavigationLoading,
      (loading, wasLoading) => {
        if (wasLoading && !loading && itemNavigationDirection.value) {
          clearItemNavigationDirectionLater();
        }
      },
    );

    return {
      resolveClapPlacement,
      overlayRef,
      videoRef,
      audioRef,
      currentIndex,
      isLoading,
      isVideoPaused,
      isMobileViewport,
      images,
      hasMultipleImages,
      showNavigationArrows,
      showNavigationCounter,
      currentMediaUrl,
      isCurrentMediaVideo,
      isAudioItem,
      audioUrl,
      audioCoverImage,
      videoPoster,
      imageAlt,
      prevImage,
      nextImage,
      playVideo,
      onMediaLoad,
      onVideoLoaded,
      onVideoPlay,
      onVideoPause,
      // Minimize / expand
      isMinimized,
      minimizeOverlay,
      expandOverlay,
      minimizedThumbnail,
      goToUserProfile,
      authorProfileIdentifier,
      // Wishlist and share
      isWishlistFeatureEnabled,
      isWishlisted,
      toggleWishlist,
      handleShare,
      handleShareToMessages,
      handleAuthorityPurchaseSettled,
      shareMenuOpen,
      favouritesMenuOpen,
      handleMessage,
      requireAuth,
      avatarData,
      authorProfileHref,
      authorFollowTargetId,
      isFollowingAuthor,
      isAuthorFollowLoading,
      authorFollowDisclosureOpen,
      selectedAuthorFollowCadence,
      authorFollowError,
      authorFollowStatus,
      requestAuthorFollowToggle,
      confirmAuthorFollow,
      followAuthorWithoutEmail,
      cancelAuthorFollowDisclosure,
      authorIdentityFollowControl,
      isFollowUiEnabled,
      // Service booking
      availableDays,
      filteredHours,
      isSlotAvailable,
      isSlotSelected,
      toggleSlot,
      handleMakeOffer,
      handleBuyOfferClick,
      buyConfirmPending,
      // New item type flags
      isPosterItem,
      isTweetItem,
      isPostItem,
      isDocumentItem,
      isProductItem,
      isServiceItem,
      isNFTType,
      formatAddress,
      isIPFSUrl,
      isTextOnlyItem,
      displayCoinSymbol,
      // New computed properties
      displayTitle,
      displayDescription,
      currentImageCaption,
      isTitleOnlyPost,
      formattedPrice,
      tweetBackgroundImage,
      postMediaUrl,
      isPostMediaVideo,
      isPostMediaAudio,
      // Multi-media post properties
      postMediaItems,
      postMediaItemsForGrid,
      presentationMode,
      isArticlePresentation,
      isStoryPresentation,
      isGalleryPresentation,
      isTextStoryPresentation,
      storyMediaItem,
      storyUsesMobileCover,
      galleryMediaItems,
      currentGalleryItem,
      fullscreenMediaItem,
      onGalleryMobileScroll,
      galleryVideoRefs,
      isMultiMediaPost,
      isMultiMediaSlideshowPost,
      isSingleMediaPost,
      isComplexPost,
      isSimplePostItem,
      blogContentBlocks,
      descriptionParagraphs,
      documentUrl,
      documentFilename,
      isPdfDocument,
      isTextDocument,
      documentExtension,
      documentTextContent,
      docDescriptionRef,
      docDescOverflows,
      // Multi-media slideshow navigation
      totalMultiSlides,
      currentMultiMediaItem,
      isOnTextSlide,
      goToTextSlide,
      goToFirstSlide,
      navigateToNextPost,
      navigateToPrevPost,
      contentTransitionName,
      slideDirection,
      // Touch swipe support
      onTouchStart,
      onTouchMove,
      onTouchEnd,
      onWheel,
      // Media zoom overlay
      showMediaZoom,
      zoomMediaIndex,
      onOrganicMediaClick,
      openMediaFullscreen,
      closeMediaZoom,
      mediaAspectByUrl,
      truncateCaption,
      // Ultra overlay
      showUltraOverlay,
      ultraMediaUrl,
      ultraMediaPoster,
      ultraMediaType,
      closeUltraOverlay,
      // Actions bar properties
      currentUserUid,
      currentOrgUID,
      isOwner,
      isAuthorityPresentation,
      itemType,
      isSoundCloudEmbed,
      isSpotifyEmbed,
      embedExternalUrl,
      isEmbedItem,
      isVerticalEmbedItem,
      sanitizedEmbedOverlay,
      isMintableType,
      isItemMinted,
      ipfsLinks,
      iconsetPath,
      openLoginModal,
      // NFT listing management
      nftListingStatus,
      showListNFTOverlay,
      isEditingListing,
      existingListingPrice,
      handleOverlayListNFT,
      handleOverlayEditListing,
      handleOverlayDelistNFT,
      handleOverlayNFTListed,
      handleOverlayNFTListingUpdated,
      closeListNFTOverlay,
      handleClose,
      // Float player conflict dialog
      conflictDialogVisible,
      conflictAddToQueue,
      conflictPauseAndPlay,
    };
  },
};
</script>

<style scoped>
.slideshow-overlay {
  position: fixed;
  inset: 0;
  z-index: 6;
  background-color: var(--color-surface-canvas, #000);
  display: flex;
  align-items: center;
  justify-content: center;
  outline: none;
  overscroll-behavior: none;
  /* Animate between expanded ↔ minimized */
  transition: inset 0.35s cubic-bezier(0.4, 0, 0.2, 1),
    background-color 0.35s ease,
    height 0.35s cubic-bezier(0.4, 0, 0.2, 1);
}

.slideshow-close {
  position: absolute;
  top: var(--space-5);
  right: var(--space-5);
  z-index: 10;
  display: grid;
  place-items: center;
  width: var(--control-height-default);
  height: var(--control-height-default);
  padding: 0;
  color: var(--color-icon-primary);
  background: transparent;
  border: 0;
  cursor: pointer;
}

.slideshow-close:hover {
  transform: none;
}

svg.slideshow-close__icon {
  width: var(--control-height-dense);
  height: var(--control-height-dense);
}

.slideshow-content {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: fit-content;
  max-width: 95vw;
  max-height: 90vh;
  /* Base transform for animation */
  transform-origin: bottom center;
}

.slideshow-content.blog-mode {
  width: 100vw;
  max-width: 100vw;
  height: 100vh;
  max-height: 100vh;
}

.slideshow-content.story-mode,
.slideshow-content.gallery-mode {
  width: 100vw;
  max-width: 100vw;
  height: 100vh;
  height: 100dvh;
  max-height: 100vh;
  max-height: 100dvh;
}

.slideshow-image-wrapper {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  max-width: 85vw;
  max-height: 85vh;
  margin-bottom: var(--space-4, 16px);
}

.slideshow-image-wrapper.blog-mode {
  max-width: 100vw;
  max-height: 100vh;
  margin-bottom: 0;
  align-items: stretch;
}

.slideshow-image-wrapper.story-mode,
.slideshow-image-wrapper.gallery-mode {
  width: 100vw;
  max-width: 100vw;
  height: 100vh;
  height: 100dvh;
  max-height: 100vh;
  max-height: 100dvh;
  margin-bottom: 0;
}

/* ========== TITLE AT TOP & CAPTION BELOW ========== */
.slideshow-title-top {
  position: fixed;
  top: var(--space-5, 24px);
  left: 50%;
  transform: translateX(-50%);
  z-index: 1002;
  max-width: 80vw;
  text-align: center;
  pointer-events: none;
}

/* Title-only posts: center title vertically and horizontally */
.slideshow-title-top.title-only-centered {
  top: 50%;
  transform: translate(-50%, -50%);
}

.slideshow-title-top.title-only-centered .slideshow-title-text {
  font-size: 1.8rem;
  font-weight: var(--font-weight-medium, 400);
}

.slideshow-title-text {
  color: var(--color-text-on-accent);
  font-size: var(--font-size-title, 1.2rem);
  font-weight: var(--font-weight-normal, 300);
  text-shadow: 0 1px 4px rgba(0, 0, 0, 0.6);
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
}

.slideshow-caption-bottom {
  position: fixed;
  bottom: calc(var(--space-5, 24px) + 60px);
  left: 50%;
  transform: translateX(-50%);
  z-index: 1002;
  max-width: 70vw;
  max-height: 25vh;
  overflow-y: auto;
  text-align: center;
  color: white;
  font-size: var(--font-size-body, 16px);
  font-weight: var(--font-weight-normal, 300);
  line-height: 1.4;
  text-shadow: 0 1px 4px rgba(0, 0, 0, 0.6);
  pointer-events: auto;
  /* fade-hint at top when scrolled */
  mask-image: linear-gradient(to bottom, transparent 0%, black 6%, black 94%, transparent 100%);
  -webkit-mask-image: linear-gradient(to bottom, transparent 0%, black 6%, black 94%, transparent 100%);
}

/* Per-image caption shown under media */
.slideshow-image-caption {
  position: fixed;
  bottom: calc(var(--space-5, 24px) + 40px);
  left: 50%;
  transform: translateX(-50%);
  z-index: 1002;
  max-width: 70vw;
  text-align: center;
  color: white;
  font-size: var(--font-size-label, 14px);
  font-style: italic;
  line-height: 1.5;
  text-shadow: 0 1px 4px rgba(0, 0, 0, 0.6);
  pointer-events: auto;
  max-height: 15vh;
  overflow-y: auto;
}

/* Container for thumbnail + spinner while video loads */
.video-loading-container {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
}

.video-loading-container .slideshow-loading {
  position: absolute;
  inset: 0;
  z-index: 5;
}

.video-loading-container .video-poster-fallback {
  position: relative;
  z-index: 1;
}

.slideshow-image {
  max-width: 85vw;
  max-height: 85vh;
  object-fit: contain;
}

/* ========== ADAPTIVE STORY / GALLERY PRESENTATIONS ========== */
.post-story-container,
.post-gallery-container {
  position: relative;
  width: 100vw;
  height: 100vh;
  height: 100dvh;
  overflow: hidden;
  background: var(--color-media-canvas);
}

.post-story-stage,
.post-gallery-carousel,
.post-gallery-item--active {
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.post-story-media {
  width: 100%;
  height: 100%;
  object-fit: contain;
  background: var(--color-media-canvas);
}

.post-story-description {
  position: absolute;
  left: 50%;
  bottom: calc(var(--space-5, 24px) + 52px);
  z-index: 4;
  width: min(
    680px,
    calc(100vw - var(--space-5, 24px) - var(--space-5, 24px))
  );
  max-height: 18vh;
  padding: var(--space-4, 16px);
  overflow-y: auto;
  transform: translateX(-50%);
  border-radius: var(--radius-sm, 8px);
  background: var(--color-overlay-scrim-strong, rgba(0, 0, 0, 0.72));
  color: #fff;
  text-align: center;
  line-height: 1.5;
}

.post-gallery-description {
  position: absolute;
  top: calc(var(--space-5, 24px) + 72px);
  left: 50%;
  z-index: 4;
  width: min(
    680px,
    calc(100vw - var(--space-5, 24px) - var(--space-5, 24px))
  );
  max-height: 14vh;
  padding: var(--space-2, 8px) var(--space-4, 16px);
  overflow-y: auto;
  transform: translateX(-50%);
  border-radius: var(--radius-sm, 8px);
  background: var(--color-overlay-scrim-strong, rgba(0, 0, 0, 0.72));
  color: #fff;
  text-align: center;
  line-height: 1.45;
}

.post-gallery-mobile-description {
  width: calc(
    100% - var(--space-4, 16px) - var(--space-4, 16px)
  );
  margin: 0 auto;
  padding: var(--space-4, 16px);
  border-radius: var(--radius-sm, 8px);
  background: var(--color-overlay-scrim-strong, rgba(0, 0, 0, 0.72));
  color: #fff;
  text-align: center;
  line-height: 1.5;
}

.post-gallery-carousel {
  overflow: hidden;
}

.post-gallery-item {
  position: relative;
  margin: 0;
}

.post-gallery-media {
  display: block;
  width: 100%;
  height: 100%;
  max-width: 85vw;
  max-height: 85vh;
  object-fit: contain;
  background: var(--color-media-canvas);
}

.post-gallery-caption {
  position: absolute;
  left: 50%;
  bottom: calc(var(--space-5, 24px) + 52px);
  z-index: 4;
  width: min(
    680px,
    calc(100vw - var(--space-5, 24px) - var(--space-5, 24px))
  );
  max-height: 14vh;
  padding: var(--space-2, 8px) var(--space-4, 16px);
  overflow-y: auto;
  transform: translateX(-50%);
  border-radius: var(--radius-sm, 8px);
  background: var(--color-overlay-scrim-strong, rgba(0, 0, 0, 0.72));
  color: #fff;
  text-align: center;
  line-height: 1.45;
}

.post-gallery-mobile-list {
  display: none;
}

@media only screen and (max-width: 590px) {
  .post-story-container.story-mobile-cover .post-story-media {
    object-fit: cover;
  }

  .post-story-description {
    bottom: calc(
      var(--control-height-compact, 36px) +
        var(--space-8, 32px) +
        env(safe-area-inset-bottom, 0px)
    );
    width: calc(
      100vw - var(--space-4, 16px) - var(--space-4, 16px)
    );
    max-height: 20vh;
    padding: var(--space-2, 8px) var(--space-4, 16px);
  }

  .post-gallery-container {
    overflow: visible;
  }

  .post-gallery-mobile-list {
    width: 100%;
    height: 100vh;
    height: 100dvh;
    display: flex;
    flex-direction: column;
    gap: var(--space-4, 16px);
    padding:
      calc(
        var(--space-8, 32px) +
          var(--space-8, 32px) +
          var(--space-8, 32px) +
          env(safe-area-inset-top, 0px)
      )
      0
      calc(
        var(--control-height-compact, 36px) +
          var(--space-8, 32px) +
          var(--space-8, 32px) +
          env(safe-area-inset-bottom, 0px)
      );
    overflow-y: auto;
    overscroll-behavior: contain;
    -webkit-overflow-scrolling: touch;
  }

  .post-gallery-item {
    width: 100%;
    min-height: 60vh;
    min-height: 60dvh;
    flex: 0 0 auto;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: var(--space-2, 8px);
    padding: 0 var(--space-4, 16px);
  }

  .post-gallery-media {
    width: 100%;
    height: auto;
    max-width: 100%;
    max-height: 82vh;
    max-height: 82dvh;
    object-fit: contain;
  }

  .post-gallery-caption {
    position: static;
    width: min(680px, 100%);
    max-height: none;
    padding: var(--space-2, 8px) var(--space-4, 16px);
    overflow: visible;
    transform: none;
  }

  .post-complex-container {
    width: 100vw;
    min-height: 100vh;
    min-height: 100dvh;
    max-height: 100vh;
    max-height: 100dvh;
    padding-right: var(--space-4, 16px);
    padding-left: var(--space-4, 16px);
    padding-bottom: calc(
      var(--control-height-compact, 36px) +
        var(--space-8, 32px) +
        var(--space-8, 32px) +
        env(safe-area-inset-bottom, 0px)
    );
    border-radius: 0;
  }

  .post-complex-media-grid {
    grid-template-columns: minmax(0, 1fr);
  }

  .post-complex-grid-item.grid-item-landscape {
    grid-column: span 1;
  }

  .post-complex-grid-element {
    max-height: 82vh;
    max-height: 82dvh;
    object-fit: contain;
  }
}

/* Video hidden while loading (thumbnail shows instead) */
.slideshow-image.video-loading {
  position: absolute;
  opacity: 0;
  pointer-events: none;
}

/* Media navigation stays close to the content and clears the item actions. */
.slideshow-nav {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background: var(--color-surface-canvas, #fff);
  border: none;
  border-radius: 50%;
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  transition: transform 0.15s ease, opacity 0.15s ease;
  flex-shrink: 0;
  z-index: 10;
}

.slideshow-nav.slideshow-prev {
  left: calc(clamp(var(--space-8), 4vw, 50px) + var(--control-height-compact) + var(--space-5));
}

.slideshow-nav.slideshow-next {
  right: calc(clamp(var(--space-8), 4vw, 50px) + var(--control-height-compact) + var(--space-5));
}

.slideshow-nav:hover:not(:disabled) {
  transform: translateY(-50%) scale(1.1);
}

.slideshow-nav:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

.slideshow-counter {
  position: fixed;
  top: calc(var(--space-5, 24px) + 40px);
  left: 50%;
  transform: translateX(-50%);
  z-index: 1002;
  color: var(--color-text-primary);
  font-size: var(--font-size-label, 14px);
  pointer-events: none;
}

.slideshow-loading {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.3);
}

.slideshow-spinner {
  width: 40px;
  height: 40px;
  border: 3px solid rgba(255, 255, 255, 0.3);
  border-top-color: #fff;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* Fade transition */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* Shrink transition for content when minimizing */
.slideshow-shrink-enter-active,
.slideshow-shrink-leave-active {
  transition: transform 0.35s cubic-bezier(0.4, 0, 0.2, 1),
    opacity 0.3s ease;
}

.slideshow-shrink-enter-from {
  transform: scale(0.15) translateY(80vh);
  opacity: 0;
}

.slideshow-shrink-leave-to {
  transform: scale(0.15) translateY(80vh);
  opacity: 0;
}

.post-up-enter-active,
.post-up-leave-active,
.post-down-enter-active,
.post-down-leave-active {
  /* Keep the keyed posts in the same viewport layer while both exist. Without
     this, the overlay's row flex layout places them side by side and turns a
     vertical transform into a visible horizontal page shift on desktop. */
  position: absolute;
  inset: 0;
  margin: auto;
  will-change: transform, opacity;
  transition:
    transform var(--motion-standard) var(--motion-easing-standard),
    opacity var(--motion-standard) var(--motion-easing-standard);
}

.post-up-enter-from {
  transform: translateY(12vh);
  opacity: 0;
}

.post-up-leave-to {
  transform: translateY(-12vh);
  opacity: 0;
}

.post-down-enter-from {
  transform: translateY(-12vh);
  opacity: 0;
}

.post-down-leave-to {
  transform: translateY(12vh);
  opacity: 0;
}

/* Video play overlay (shown when paused) */
.video-play-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.3);
  cursor: pointer;
  transition: background 0.2s ease;
}

.video-play-overlay:hover {
  background: rgba(0, 0, 0, 0.4);
}

.video-play-overlay:hover .play-button {
  transform: scale(1.1);
}

.play-button {
  width: 72px;
  height: 72px;
  background: rgba(255, 255, 255, 0.95);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
  transition: transform 0.2s ease;
}

.play-icon {
  width: 32px;
  height: 32px;
  margin-left: 4px; /* Visual centering for play triangle */
  color: var(--color-text-primary, #000);
}

/* Mobile adjustments */
@media only screen and (max-width: 590px) {
  .slideshow-nav {
    width: 36px;
    height: 36px;
  }

  .slideshow-nav.slideshow-prev {
    left: var(--space-7);
  }

  .slideshow-nav.slideshow-next {
    right: var(--space-7);
  }

  .slideshow-close {
    top: var(--space-2, 8px);
    right: var(--space-2, 8px);
  }

  .slideshow-image {
    max-width: 90vw;
  }

  .slideshow-caption-bottom {
    width: 85vw;
    max-width: 85vw;
    max-height: 18vh;
  }

  .slideshow-image-caption {
    width: 85vw;
    max-width: 85vw;
    max-height: 12vh;
  }

  .play-button {
    width: 56px;
    height: 56px;
  }

  .play-icon {
    width: 24px;
    height: 24px;
  }
}

/* ========== AUDIO SLIDESHOW STYLES ========== */
.audio-slideshow-container {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  max-width: 85vw;
  max-height: 85vh;
  overflow: hidden;
}

.audio-slideshow-container .slideshow-image.audio-cover {
  max-width: 100%;
  max-height: calc(85vh - 80px); /* Leave room for audio player */
  width: auto;
  height: auto;
  border-radius: 0;
  box-shadow: none;
}

/* Placeholder when no cover image */
.audio-placeholder {
  width: 300px;
  height: 300px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(
    135deg,
    var(--color-surface-raised, #fefcf7) 0%,
    var(--color-surface-canvas, #f0f0f5) 100%
  );
}

.audio-placeholder-icon {
  width: 120px;
  height: 120px;
  color: var(--color-border-subtle, #cbcbcb);
}

/* Audio player at bottom */
.audio-slideshow-player {
  width: 100%;
  padding: var(--space-4, 16px);
  background: var(--color-surface-canvas, #fff);
  border-top: 1px solid var(--color-surface-subtle, #e5e5e5);
}

.audio-slideshow-container:has(> .audio-cover) .audio-slideshow-player {
  padding-left: 0;
  padding-right: 0;
}

.slideshow-audio-element {
  width: 100%;
  height: 40px;
}

/* Mobile adjustments for audio */
@media only screen and (max-width: 590px) {
  .audio-placeholder {
    width: 250px;
    height: 250px;
  }

  .audio-placeholder-icon {
    width: 80px;
    height: 80px;
  }

  .audio-slideshow-player {
    padding: var(--space-2, 8px) var(--space-4, 16px);
  }
}

/* ========== EMBED SLIDESHOW STYLES ========== */
.embed-slideshow-container {
  width: 85vw;
  max-width: 800px;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.spotify-link-card {
  width: 100%;
  min-height: 240px;
  border-radius: var(--radius-sm, 8px);
  padding: clamp(20px, 4vw, 32px);
  background:
    radial-gradient(circle at top left, rgba(29, 185, 84, 0.24), transparent 40%),
    linear-gradient(135deg, rgba(18, 18, 18, 0.98), rgba(32, 32, 32, 0.94));
  border: 1px solid rgba(29, 185, 84, 0.24);
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 12px;
}

.spotify-link-card--overlay {
  max-width: 720px;
}

.spotify-link-card__eyebrow {
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.68);
}

.spotify-link-card__title {
  margin: 0;
  font-size: clamp(22px, 3.6vw, 34px);
  line-height: 1.1;
  color: #fff;
}

.spotify-link-card__button {
  width: fit-content;
  padding: 10px 16px;
  border-radius: 999px;
  background: #1db954;
  color: #04130a;
  font-weight: 700;
  text-decoration: none;
}

.spotify-link-card__button:hover {
  filter: brightness(1.05);
}

.embed-slideshow-player {
  width: 100%;
  aspect-ratio: 16 / 9;
  border-radius: var(--radius-sm, 8px);
  overflow: hidden;
}

.embed-slideshow-player :deep(iframe) {
  width: 100%;
  height: 100%;
  border: none;
}

.embed-slideshow-player.embed-vertical {
  aspect-ratio: 9 / 16;
  max-height: 85vh;
  width: auto;
  max-width: 100%;
  margin: 0 auto;
}

/* SoundCloud: no forced 16:9 — let the widget use its natural height */
.embed-slideshow-container.soundcloud-embed .embed-slideshow-player {
  aspect-ratio: auto;
  min-height: 166px;
}
.embed-slideshow-container.soundcloud-embed .embed-slideshow-player :deep(iframe) {
  height: 166px;
}

/* ========== POSTER SLIDESHOW STYLES ========== */
.poster-slideshow-container {
  width: 85vw;
  max-width: 600px;
  min-height: 300px;
  max-height: 85vh;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  padding: var(--space-7, 32px);
}

.poster-content {
  text-align: left;
}

.poster-title {
  font-size: var(--font-size-display-large, 28px);
  font-weight: 600;
  color: var(--color-text-primary, #373737);
  line-height: 1.3;
  margin: 0;
}

/* ========== TWEET SLIDESHOW STYLES ========== */
.tweet-slideshow-container {
  position: relative;
  width: 85vw;
  max-width: 500px;
  min-height: 200px;
  display: flex;
  align-items: flex-start;
  justify-content: flex-start;
  overflow: hidden;
}

.tweet-background-image {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  z-index: 0;
}

.tweet-content {
  position: relative;
  z-index: 1;
  padding: var(--space-7, 32px);
  text-align: left;
  width: 100%;
}

.tweet-content.with-image {
  background: rgba(0, 0, 0, 0.5);
  width: 100%;
  min-height: 200px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
}

.tweet-content.with-image .tweet-title,
.tweet-content.with-image .tweet-text {
  color: #fff;
}

.tweet-title {
  font-size: var(--font-size-display, 24px);
  font-weight: 600;
  color: var(--color-text-primary, #373737);
  margin: 0 0 var(--space-4, 16px) 0;
}

.tweet-text {
  font-size: var(--font-size-body, 16px);
  color: var(--color-text-secondary, #4f4f4f);
  margin: 0;
  line-height: 1.5;
}

/* ========== COMPLEX POST (BLOG-STYLE) STYLES ========== */

.post-complex-container {
  display: flex;
  flex-direction: column;
  width: min(900px, calc(100vw));
  max-height: 100vh;
  padding: var(--space-5);
  overflow-y: auto;
  overflow-x: hidden;
  padding-top: calc(
    var(--space-8, 32px) +
      var(--space-8, 32px) +
      var(--space-8, 32px) +
      var(--space-4, 16px)
  );
  padding-bottom: var(--space-8, 32px);
  background-color: var(--color-surface-canvas, #fff);
  border-radius: var(--radius-sm, 12px);
  scrollbar-width: thin;
  scrollbar-color: var(--color-scrollbar-thumb-subtle) transparent;
}

.post-complex-container::-webkit-scrollbar {
  width: 6px;
}

.post-complex-container::-webkit-scrollbar-track {
  background: transparent;
}

.post-complex-container::-webkit-scrollbar-thumb {
  background-color: var(--color-scrollbar-thumb-subtle);
  border-radius: 3px;
}

.post-complex-media-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--space-5, 10px);
  width: 100%;
  flex-shrink: 0;
}

.post-complex-grid-item {
  overflow: hidden;
  cursor: pointer;
  min-height: 0;
}

.post-complex-grid-item.grid-item-landscape {
  grid-column: span 2;
}

.post-complex-grid-media-wrap {
  position: relative;
  overflow: hidden;
  border-radius: var(--radius-sm);
}

.grid-item-landscape .post-complex-grid-media-wrap {
}

.post-complex-grid-element {
  width: 100%;
  height: auto;
  display: block;
}

.post-complex-play-overlay {
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  width: 56px;
  height: 56px;
  display: grid;
  place-items: center;
  background: rgba(0, 0, 0, 0.45);
  border-radius: 50%;
  pointer-events: none;
}

.post-complex-play-icon {
  width: 32px;
  height: 32px;
  color: #fff;
}

.post-complex-title {
  font-family: var(--font-family-ui);
  font-size: var(--font-size-display);
  line-height: 1.05;
  color: var(--color-text-primary, #1a1a1a);
  padding:
    0
    0
    calc(var(--space-8, 32px) + var(--space-8, 32px));
  word-wrap: break-word;
  overflow-wrap: break-word;
}

.post-complex-caption {
  display: block;
    color: var(--color-text-subtle);
  font-size: var(--font-size-body, 14px);
  line-height: 1.3;
  white-space: pre-line;
  word-wrap: break-word;
  overflow-wrap: break-word;

    padding-top: var(--space-1);
    padding-bottom: var(--space-4);
}

.post-complex-hero {
  width: 100%;
  flex-shrink: 0;
}

.post-complex-hero-element {
  width: 100%;
  object-fit: contain;
  display: block;
}

.post-complex-body {
  padding: var(--space-5, 24px) 0;
  display: flex;
  flex-direction: column;
  gap: var(--space-5, 24px);
}

.post-complex-paragraph {
  font-size: var(--font-size-body, 16px);
  line-height: 1.6;
  color: var(--color-text-primary, #1a1a1a);
  word-wrap: break-word;
  overflow-wrap: break-word;
}

.post-complex-paragraph :deep(a) {
  color: #3b82f6;
  text-decoration: none;
}

.post-complex-paragraph :deep(a:hover) {
  text-decoration: underline;
}

.post-complex-embed-wrapper {
  position: relative;
  width: 100%;
  padding-bottom: 56.25%; /* 16:9 aspect ratio */
  border-radius: var(--space-4, 8px);
  overflow: hidden;
}

.post-complex-embed-wrapper :deep(iframe) {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border: none;
}

/* ========== POST SLIDESHOW STYLES ========== */

/* Single media post - large display like standard images */
.post-single-media-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: var(--space-5, 24px);
}

.post-single-media {
  display: flex;
  align-items: center;
  justify-content: center;
}

.post-single-media-element {
  max-width: 85vw;
  max-height: 70vh;
  object-fit: contain;
}

.post-single-audio-element {
  width: 400px;
  max-width: 85vw;
}

.post-single-description {
  max-width: 700px;
  padding: 0 var(--space-5, 24px);
  text-align: center;
  color: var(--color-text-primary);
  font-size: var(--font-size-body, 16px);
  line-height: 1.6;
  max-height: 25vh;
  overflow-y: auto;
}

/* ===== MULTI-MEDIA SLIDESHOW LAYOUT ===== */
.post-multi-slideshow-container {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  overflow: hidden;
  position: relative;
}

.post-multi-media-element {
  max-width: 85vw;
  max-height: 85vh;
  object-fit: contain;
}

.post-multi-audio-element {
  width: 400px;
  max-width: 85vw;
}

/* Slide transition animations */
.slide-left-enter-active,
.slide-left-leave-active,
.slide-right-enter-active,
.slide-right-leave-active {
  transition: transform 0.3s ease-out, opacity 0.3s ease-out;
}

/* Slide left (next) - new content comes from right */
.slide-left-enter-from {
  transform: translateX(100%);
  opacity: 0;
}
.slide-left-leave-to {
  transform: translateX(-100%);
  opacity: 0;
}

/* Slide right (prev) - new content comes from left */
.slide-right-enter-from {
  transform: translateX(-100%);
  opacity: 0;
}
.slide-right-leave-to {
  transform: translateX(100%);
  opacity: 0;
}

/* Text slide (last slide in multi-media posts) */
.post-text-slide {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  max-width: 700px;
  padding: var(--space-8, 32px);
  text-align: center;
}

.post-text-slide-title {
  font-size: 2rem;
  font-weight: var(--font-weight-display-light, 400);
  color: var(--color-text-on-media);
  margin: 0 0 var(--space-5, 24px) 0;
  line-height: 1.3;
}

.post-text-slide-description {
  font-size: var(--font-size-body, 16px);
  color: var(--color-text-on-media);
  line-height: 1.7;
  max-height: 60vh;
  overflow-y: auto;
  text-align: left;
}

.post-text-slide-empty {
  font-size: var(--font-size-body, 16px);
  color: var(--color-text-secondary, #999);
  font-style: italic;
}

/* ===== ORGANIC MEDIA GRID LAYOUT ===== */
.post-organic-container {
  width: 100vw;
  max-width: 900px;
  height: 100vh;
  max-height: 100vh;
  overflow-y: auto;
  overflow-x: hidden;
  overscroll-behavior: contain;
  margin: 0 auto;
  padding: var(--space-5, 24px);
  /* Avoid content being obscured by fixed title/actions */
  padding-top: calc(var(--space-5, 24px) + 48px);
  padding-bottom: calc(var(--space-5, 24px) + 120px);
  display: flex;
  flex-direction: column;
  gap: var(--space-5, 24px);
}

.post-organic-title {
  font-size: 1.5rem;
  font-weight: var(--font-weight-normal, 500);
  color: var(--color-text-on-media);
  margin: 0;
  text-align: left;
  line-height: 1.3;
}

.post-organic-description {
  font-size: var(--font-size-body, 16px);
  color: var(--color-text-on-media);
  line-height: 1.7;
  text-align: left;
}

/* ===== END ORGANIC MEDIA GRID LAYOUT ===== */

/* Multi-media blog-style layout (DEPRECATED - kept for reference) */
.post-blog-container {
  width: 100vw;
  max-width: 900px;
  height: 100vh;
  max-height: 100vh;
  overflow-y: auto;
  overflow-x: hidden;
  overscroll-behavior: contain;
  margin: 0 auto;
}

.post-blog-content {
  display: flex;
  flex-direction: column;
  gap: var(--space-8, 32px);
  padding: var(--space-5, 24px);
  /* Avoid content being obscured by fixed title/actions */
  padding-top: calc(var(--space-5, 24px) + 48px);
  padding-bottom: calc(var(--space-5, 24px) + 120px);
  align-items: center;
}

.post-blog-paragraph {
  font-size: var(--font-size-body, 16px);
  color: var(--color-text-on-media);
  line-height: 1.7;
  margin: 0;
  text-align: left;
  width: 100%;
  max-width: 600px;
}

.post-blog-media {
  width: 100%;
  max-width: 900px;
  display: flex;
  flex-direction: column;
  gap: 0;
  align-items: flex-start;
}

.post-blog-caption {
  margin: var(--space-2, 8px) 0 0 0;
  font-size: var(--font-size-label, 14px);
  color: var(--color-text-on-media);
  opacity: 0.9;
  line-height: 1.5;
  text-align: left;
  white-space: pre-wrap;
}

.post-blog-image {
  max-width: 100%;
  max-height: 60vh;
  object-fit: contain;
  border-radius: var(--radius-lg, 18px);
}

.post-blog-video {
  max-width: 100%;
  max-height: 60vh;
  border-radius: var(--radius-lg, 18px);
}

.post-blog-audio {
  width: 100%;
  max-width: 500px;
  border-radius: var(--radius-md, 10px);
}

/* Hide scrollbars on desktop for the blog container */
@media (min-width: 601px) {
  .post-blog-container {
    scrollbar-width: none;
  }
  .post-blog-container::-webkit-scrollbar {
    width: 0;
    height: 0;
    display: none;
  }
}

.post-blog-document {
  display: inline-flex;
  align-items: center;
  gap: var(--space-2, 8px);
  padding: var(--space-4, 16px) var(--space-5, 24px);
  background: var(--color-surface-canvas, rgba(255, 255, 255, 0.1));
  color: var(--color-text-on-media);
  text-decoration: none;
  border-radius: var(--radius-sm, 8px);
  transition: background 0.15s ease;
}

.post-blog-document:hover {
  background: var(--color-surface-subtle, rgba(255, 255, 255, 0.2));
}

.post-blog-document .small {
  stroke: var(--color-text-on-media);
}

/* Text-only post layout */
.post-slideshow-container {
  width: 85vw;
  max-width: 700px;
  max-height: 85vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.post-text-section {
  flex: 1;
  padding: var(--space-5, 24px);
  overflow-y: auto;
  max-height: 85vh;
  box-shadow: var(--shadow-surface-recessed);
  border-radius: var(--radius-sm, 8px);
}

.post-title {
  font-size: var(--font-size-display, 24px);
  font-weight: 600;
  color: var(--color-text-on-media);
  margin: 0 0 var(--space-4, 16px) 0;
}

.post-description {
  font-size: var(--font-size-body, 16px);
  color: var(--color-text-on-media);
  line-height: 1.6;
  white-space: pre-wrap;
}

/* Mobile adjustments for post layouts */
@media only screen and (max-width: 590px) {
  .post-single-media-element {
    max-width: 95vw;
    max-height: 80vh;
  }

  .post-single-audio-element {
    width: 90vw;
  }

  .post-blog-container {
    width: 100vw;
    height: 100vh;
    max-height: 100vh;
  }

  .post-blog-content {
    padding: var(--space-4, 16px);
    gap: var(--space-4, 16px);
  }

  .post-blog-image {
    max-height: 50vh;
  }

  .post-blog-video {
    max-height: 50vh;
  }

  .post-slideshow-container {
    width: 95vw;
  }

  .post-text-section {
    padding: var(--space-4, 16px);
  }
}

/* ========== DOCUMENT SLIDESHOW STYLES ========== */
.document-slideshow-container {
  width: 85vw;
  max-width: 900px;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: var(--space-7, 32px) var(--space-7, 32px) 0;
  gap: var(--space-5, 24px);
}

.document-icon-wrapper {
  width: 180px;
  height: 180px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--color-surface-raised, #fefcf7);
  border-radius: var(--radius-sm, 8px);
}

.document-icon {
  width: 48px;
  height: 48px;
  stroke: var(--color-icon-muted);
}

.document-filename {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.5);
  word-break: break-word;
}

.document-description {
  max-width: 700px;
  padding: 0 var(--space-5, 24px);
  text-align: center;
  color: var(--color-text-primary);
  font-size: var(--font-size-body, 16px);
  line-height: 1.6;
  max-height: 25vh;
  overflow-y: auto;
  box-shadow: var(--shadow-surface-recessed);
  border-radius: var(--radius-sm, 8px);
}
.document-description.is-overflowing {
  box-shadow: var(--shadow-surface-recessed);
  border-radius: var(--radius-sm, 8px);
}

/* PDF inline viewer */
.document-pdf-viewer {
  width: 100%;
  max-width: 800px;
  aspect-ratio: 1 / 1.414; /* A4 paper ratio */
  max-height: 75vh;
  border-radius: var(--radius-sm, 8px);
  overflow: hidden;
  background: #fff;
}

.document-pdf-embed {
  width: 100%;
  height: 100%;
  border: none;
  border-radius: var(--radius-sm, 8px);
}

/* Text file viewer */
.document-text-viewer {
  width: 100%;
  max-width: 800px;
  max-height: 75vh;
  border-radius: var(--radius-sm, 8px);
  overflow: hidden;
  background: var(--color-surface-raised, #fefcf7);
  display: flex;
  flex-direction: column;
}

.document-text-header {
  display: flex;
  align-items: center;
  gap: var(--space-2, 8px);
  padding: var(--space-4, 12px) var(--space-5, 16px);
  border-bottom: 1px solid var(--color-surface-subtle, #e5e5e5);
  background: var(--color-surface-highlight-faint);
}

.document-text-name {
  font-size: var(--font-size-label, 13px);
  color: var(--color-text-primary);
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.document-text-body {
  flex: 1;
  overflow-y: auto;
  padding: var(--space-5, 16px);
  margin: 0;
  font-size: var(--font-size-label, 13px);
  line-height: 1.6;
  color: var(--color-text-primary);
  white-space: pre-wrap;
  word-break: break-word;
  font-family: "SF Mono", "Fira Code", "Fira Mono", "Roboto Mono", monospace;
}

/* Document type badge (reusable) */
.document-slideshow-container .document-type-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: var(--font-size-caption, 11px);
  font-weight: 600;
  background: var(--color-surface-raised);
  color: var(--color-text-muted);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  flex-shrink: 0;
}

.document-info {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--space-2, 6px);
  text-align: center;
}

.document-download-btn {
  display: flex;
  align-items: center;
  gap: var(--space-2, 8px);
  padding: var(--space-4, 16px) var(--space-5, 24px);
  background: var(--color-text-primary, #373737);
  color: #fff;
  border-radius: var(--radius-sm, 8px);
  text-decoration: none;
  font-size: var(--font-size-body, 16px);
  font-weight: 500;
  transition: background 0.15s ease;
}

.document-download-btn:hover {
  background: var(--color-text-secondary, #4f4f4f);
}

.download-icon {
  width: 20px;
  height: 20px;
  stroke: #fff;
}

/* ========== PRODUCT SLIDESHOW STYLES ========== */
.product-slideshow-container {
  width: 85vw;
  max-width: 800px;
  max-height: 85vh;
  display: flex;
  flex-direction: row;
  overflow: hidden;
}

.product-gallery {
  position: relative;
  flex: 1;
  min-width: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.product-image {
  width: 100%;
  height: 100%;
  max-height: 85vh;
  object-fit: contain;
}

.product-info-panel {
  flex: 1;
  padding: var(--space-5, 24px);
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: var(--space-4, 16px);
}

.product-title {
  font-size: var(--font-size-display, 24px);
  font-weight: 600;
  color: var(--color-text-primary, #373737);
  margin: 0;
}

.product-price {
  display: flex;
  align-items: baseline;
  gap: var(--space-2, 8px);
}

.price-amount {
  font-size: 32px;
  font-weight: 700;
  color: var(--color-text-primary, #373737);
}

.price-currency {
  font-size: var(--font-size-body, 16px);
  color: var(--color-text-muted);
}

.product-description {
  font-size: var(--font-size-body, 16px);
  color: var(--color-text-secondary, #4f4f4f);
  line-height: 1.5;
  margin: 0;
}

.product-stock,
.product-condition {
  font-size: var(--font-size-label, 0.85rem);
  color: var(--color-text-muted);
}

.make-offer-button {
  margin-top: var(--space-5, 10px);
  display: inline-flex;
  align-items: center;
  gap: var(--space-2, 8px);
  width: fit-content;
}

.make-offer-button.unit-button {
  padding: var(--space-4);
  border-color: var(--color-border-control);
  background: transparent;
  color: var(--color-text-primary);
  font-family: var(--font-family-ui);
  font-weight: var(--font-weight-normal);
}

.make-offer-button .compactImg {
  flex-shrink: 0;
}

.make-offer-button.unit-button:not(.confirm-phase):not(:disabled):hover {
  background: transparent;
  transform: translateY(var(--motion-pressed-offset));
}

.make-offer-button.confirm-phase,
.make-offer-button.unit-button.confirm-phase:not(:disabled):hover {
  background: #22c55e;
  color: #fff;
}

.make-offer-button.confirm-phase .compactImg {
  stroke: #fff;
}

/* ========== NFT OVERLAY STYLES ========== */

.nft-overlay {
  gap: var(--space-4, 16px);
}

.nft-collection-label {
  font-size: var(--font-size-label, 0.85rem);
  color: var(--color-text-muted);
  margin-top: calc(var(--space-1, 4px) * -1);
}

/* ========== SERVICE SLIDESHOW STYLES ========== */
.service-slideshow-container {
  width: 85vw;
  max-width: 800px;
  max-height: 85vh;
  display: flex;
  flex-direction: row;
  overflow: hidden;
}

.service-gallery {
  position: relative;
  flex: 1;
  min-width: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.service-image {
  width: 100%;
  height: 100%;
  max-height: 85vh;
  object-fit: contain;
}

.service-info-panel {
  flex: 1;
  padding: var(--space-5, 24px);
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: var(--space-4, 16px);
}

.service-title {
  font-size: var(--font-size-display, 24px);
  font-weight: 600;
  color: var(--color-text-primary, #373737);
  margin: 0;
}

.service-price {
  display: flex;
  align-items: baseline;
  gap: var(--space-2, 8px);
}

.service-description {
  font-size: var(--font-size-body, 16px);
  color: var(--color-text-secondary, #4f4f4f);
  line-height: 1.5;
  margin: 0;
}

.service-duration,
.service-availability {
  font-size: var(--font-size-label, 14px);
  color: var(--color-text-muted);
}

/* ========== SERVICE BOOKING TABLE ========== */
.service-booking-section {
  margin-top: var(--space-4, 16px);
}

.booking-instruction {
  font-size: var(--font-size-label, 14px);
  color: var(--color-text-secondary, #4f4f4f);
  margin-bottom: var(--space-4, 16px);
}

.booking-instruction.gray {
  color: var(--color-text-muted);
}

.availability-table {
  width: 100%;
  border-collapse: collapse;
  font-size: var(--font-size-label, 14px);
}

.availability-table th,
.availability-table td {
  padding: var(--space-2, 8px);
  text-align: center;
  border: 1px solid var(--color-border-default);
}

.availability-table th {
  background-color: var(--color-surface-canvas, #f5f5f5);
  font-weight: 600;
  color: var(--color-text-secondary, #4f4f4f);
}

.availability-table td {
  background-color: var(--color-surface-canvas, #fff);
  color: var(--color-text-primary, #373737);
  transition: background-color 0.2s ease;
}

.availability-table td.clickable {
  cursor: pointer;
}

.availability-table td.clickable:hover {
  background-color: var(--color-surface-panel);
}

.availability-table td.gray {
  background-color: var(--color-surface-panel);
  color: var(--color-text-muted);
  cursor: not-allowed;
}

.availability-table td.selected-slot {
  background-color: var(--color-accent-selection, #9c27b0);
  color: var(--color-text-on-accent-selection);
}

.availability-table td.selected-slot strong {
  color: var(--color-text-on-accent-selection);
}

/* ========== MOBILE ADJUSTMENTS FOR NEW LAYOUTS ========== */
@media only screen and (max-width: 590px) {
  .poster-slideshow-container {
    width: 90vw;
    padding: var(--space-5, 24px);
  }

  .poster-title {
    font-size: var(--font-size-display, 24px);
  }

  .tweet-slideshow-container {
    width: 90vw;
  }

  .post-slideshow-container {
    width: 90vw;
  }

  .document-slideshow-container {
    width: 90vw;
    padding: var(--space-5, 24px);
  }

  .document-pdf-viewer {
    max-height: 55vh;
  }

  .document-text-viewer {
    max-height: 50vh;
  }

  .document-description {
    max-height: 20vh;
  }

  .product-slideshow-container,
  .service-slideshow-container {
    width: 90vw;
    flex-direction: column;
  }

  .product-gallery,
  .service-gallery {
    min-width: 100%;
    max-height: 40vh;
  }

  .product-image,
  .service-image {
    max-height: 40vh;
  }

  .product-info-panel,
  .service-info-panel {
    max-height: 45vh;
  }

  .slideshow-actions-bar {
    top: auto;
    bottom: var(--space-4);
    right: var(--space-5);
    transform: none;
    flex-direction: row;
    gap: var(--space-1);
  }

  .nav-separator {
    height: auto;
    width: var(--space-4);
  }

  /* Hide button text on small screens (no hover expand) */
  .slideshow-actions-bar .button-text {
    max-width: 0;
    opacity: 0;
  }

  .slideshow-owner-avatar {
    left: var(--space-4);
    top: var(--space-4);
  }
}

/* ========== OWNER AVATAR STYLES ========== */
.slideshow-owner-avatar {
  position: fixed;
  top: var(--space-5);
  left: var(--space-5);
  z-index: 1002;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: var(--space-4);
  max-width: calc(100vw - var(--space-7) - var(--space-7));
}

.slideshow-owner-profile {
  min-width: 0;
  max-width: 100%;
  color: inherit;
  text-decoration: none;
  cursor: pointer;
  border-radius: var(--radius-sm);
}

.slideshow-owner-identity {
  display: inline-flex;
  min-width: 0;
  max-width: 100%;
}

.slideshow-owner-profile:focus-visible {
  outline: 2px solid var(--color-accent-emphasis);
  outline-offset: 2px;
}

.slideshow-follow-action {
  position: relative;
  display: inline-flex;
  align-items: center;
  flex: 0 0 auto;
}

.slideshow-follow-feedback {
  position: absolute;
  top: calc(100% + var(--space-2));
  left: 0;
  width: min(52ch, calc(100vw - var(--space-7) - var(--space-7)));
  color: var(--color-text-secondary);
  font-size: var(--font-size-caption);
}

/* ========== ACTIONS BAR STYLES ========== */
.slideshow-actions-bar {
  position: fixed;
  top: 50%;
  right: var(--space-1);
  transform: translateY(-50%);
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  justify-content: center;
  gap: var(--space-2);
  z-index: 1002;
}

.nav-separator {
  height: var(--space-4);
}

/* Hide button text by default, reveal on hover */
.slideshow-actions-bar .button-text {
  display: none;
}

.slideshow-actions-bar .slideshow-action-button:hover .button-text {
  display: inline;
}

/* Circle buttons: min-width gives circle, text max-width drives smooth growth */
.slideshow-actions-bar .slideshow-action-button {
  width: auto !important;
  min-width: var(--control-height-compact);
  height: var(--control-height-compact) !important;
  padding: 0 !important;
  gap: 0;
  justify-content: center;
  align-items: center;
  flex-direction: row-reverse;
  white-space: nowrap;
  border-radius: var(--radius-pill) !important;
  background-color: var(--color-surface-canvas) !important;
  border: 1px solid var(--color-border-control);
  box-shadow: none;
  transform: none !important;
  overflow: hidden;
  font-family: var(--font-family-ui);
  font-size: var(--font-size-body);
  font-weight: var(--font-weight-normal);
  line-height: normal;
}

.slideshow-actions-bar .slideshow-action-button:hover {
  background-color: var(--color-surface-canvas) !important;
  filter: brightness(1.35);
  transform: none !important;
}

.slideshow-actions-bar .slideshow-action-button:has(.button-text):hover {
  padding: 0 var(--space-4) !important;
  gap: var(--space-2);
}

/* Active state for favourite button */
.slideshow-actions-bar .slideshow-action-button.active {
  background-color: var(--color-surface-canvas);
}

.slideshow-actions-bar .slideshow-action-button.active .compactImg {
  fill: var(--color-favourite-active-icon);
  stroke: var(--color-favourite-active-icon);
}

/* Disabled state */
.slideshow-actions-bar .slideshow-action-button.disabled {
  opacity: 0.4;
  cursor: not-allowed;
  pointer-events: none;
}

.slideshow-actions-bar .slideshow-action-button:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

/* ClapSimple integration - match circle button style */
.slideshow-actions-bar :deep(.clap-simple) {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0;
  width: var(--control-height-compact);
  height: var(--control-height-compact);
  padding: 0;
  background-color: var(--color-surface-canvas);
  border: 1px solid var(--color-border-control);
  border-radius: 50%;
  cursor: pointer;
  overflow: hidden;
  transition: width 0.25s ease, border-radius 0.25s ease, padding 0.25s ease, gap 0.25s ease, background-color 0.15s ease;
}

.slideshow-actions-bar :deep(.clap-simple:hover),
.slideshow-actions-bar :deep(.clap-simple:focus-visible) {
  width: auto;
  padding: 0 var(--space-4);
  gap: var(--space-2);
  border-radius: var(--radius-pill);
  background-color: var(--color-surface-canvas);
}

.slideshow-actions-bar :deep(.clap-label) {
  display: none;
  font-size: var(--font-size-label);
  font-weight: var(--font-weight-normal);
}

.slideshow-actions-bar :deep(.clap-simple:hover .clap-label),
.slideshow-actions-bar :deep(.clap-simple:focus-visible .clap-label) {
  display: inline;
}

.slideshow-actions-bar :deep(.clap-icon) {
  width: 20px;
  height: 20px;
}

.slideshow-actions-bar :deep(.clap-count) {
  font-size: var(--font-size-label);
  font-weight: var(--font-weight-normal);
  color: var(--color-text-primary);
}

/* Share dropdown */
.share-dropdown-wrapper {
  position: relative;
}

.share-dropdown-menu {
  position: absolute;
  bottom: calc(100% + var(--space-1));
  right: 0;
  background: var(--color-surface-canvas);
  box-shadow: var(--shadow-surface-floating);
  border: 1px solid var(--color-border-control);
  border-radius: var(--radius-sm);
  padding: var(--space-2);
  z-index: 10;
  color: var(--color-text-primary);
}

.share-dropdown-menu .actions-menu {
  display: flex;
  flex-direction: column;
  gap: 0;
  min-width: 140px;
}

.share-dropdown-menu .menu-item {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-4);
  cursor: pointer;
  transition: background-color 0.2s ease;
  white-space: nowrap;
  font-size: var(--font-size-label);
  border-radius: var(--radius-sm);
  color: var(--color-text-primary);
}

.share-dropdown-menu .menu-item:hover {
  background-color: var(--color-surface-menu-hover);
}

.share-dropdown-menu .menu-item svg.tinyImg {
  flex-shrink: 0;
}

/* Mobile actions stay icon-only and fixed-size, including sticky touch hover. */
@media only screen and (max-width: 590px) {
  .slideshow-actions-bar .slideshow-action-button,
  .slideshow-actions-bar .slideshow-action-button:hover,
  .slideshow-actions-bar .slideshow-action-button:focus-visible,
  .slideshow-actions-bar .slideshow-action-button:has(.button-text):hover {
    width: var(--control-height-compact) !important;
    min-width: var(--control-height-compact);
    max-width: var(--control-height-compact);
    padding: 0 !important;
    gap: 0;
  }

  .slideshow-actions-bar :deep(.clap-simple),
  .slideshow-actions-bar :deep(.clap-simple:hover),
  .slideshow-actions-bar :deep(.clap-simple:focus-visible) {
    width: var(--control-height-compact);
    min-width: var(--control-height-compact);
    max-width: var(--control-height-compact);
    padding: 0;
    gap: 0;
    border-radius: 50%;
  }

  .slideshow-actions-bar :deep(.clap-simple .clap-label),
  .slideshow-actions-bar :deep(.clap-simple:hover .clap-label),
  .slideshow-actions-bar :deep(.clap-simple:focus-visible .clap-label) {
    display: none;
  }
}

/* Minted indicator - replaces mint button when item is on-chain */
.minted-indicator {
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-4) var(--space-5);
  background-color: var(--color-accent-emphasis);
  border-radius: var(--radius-pill);
  color: var(--color-text-on-accent);
  font-size: var(--font-size-label);
  font-weight: var(--font-weight-medium);
}

.minted-indicator-icon {
  width: 16px;
  height: 16px;
  color: white;
}

/* ========== MINIMIZED OVERLAY STATE ========== */
.slideshow-overlay.minimized {
  inset: auto 0 0 0;
  height: auto;
  align-items: flex-end;
  justify-content: flex-start;
  background-color: transparent;
  pointer-events: none;
}

/* Hide expanded-only children when minimized (use opacity for smooth fade) */
.slideshow-overlay.minimized .slideshow-title-top,
.slideshow-overlay.minimized .slideshow-caption-bottom,
.slideshow-overlay.minimized .slideshow-image-caption,
.slideshow-overlay.minimized .slideshow-counter,
.slideshow-overlay.minimized .slideshow-owner-avatar,
.slideshow-overlay.minimized .slideshow-actions-bar {
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.2s ease;
}

/* Add base transition for these elements to fade in on expand */
.slideshow-title-top,
.slideshow-caption-bottom,
.slideshow-image-caption,
.slideshow-counter,
.slideshow-owner-avatar,
.slideshow-actions-bar {
  transition: opacity 0.2s ease;
}

/* Minimized bar — sits at the very bottom, click-through to content below */
.slideshow-minimized-bar {
  display: flex;
  align-items: center;
  gap: var(--space-4);
  padding: var(--space-5);
  width: 100%;
  pointer-events: none;
  position: relative;
}

.slideshow-minimized-avatar {
  flex-shrink: 0;
  pointer-events: auto;
  color: inherit;
  text-decoration: none;
  cursor: pointer;
}

.slideshow-minimized-avatar--static {
  cursor: default;
}

.slideshow-minimized-thumb {
  flex-shrink: 0;
  width: var(--control-height-compact);
  height: var(--control-height-compact);
  border-radius: var(--radius-sm);
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  pointer-events: auto;
  transition: opacity 0.15s ease;
}

.slideshow-minimized-thumb:hover {
  opacity: 0.8;
}

.slideshow-minimized-thumb-fallback {
  background-color: var(--color-surface-canvas);
  box-shadow: var(--shadow-surface-recessed-compact);
}

.slideshow-minimized-thumb-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}




</style>
